import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { getBackendUrl } from '../../api';
import { FaUserTie, FaExclamationTriangle, FaChevronDown, FaChevronUp, FaCamera, FaUpload, FaTimes } from "react-icons/fa";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Swal from 'sweetalert2';
import WorkerTickets from './WorkerTickets';
import TimePicker from './TimePicker';
/* ─────────────────────────────────────────────
   Responsive hook
───────────────────────────────────────────── */
const useWindowSize = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
};


const isNotifUnread = (n) => !(n?.isRead ?? n?.is_read ?? n?.read);

/* ─────────────────────────────────────────────
   Styles — computed from current breakpoint
───────────────────────────────────────────── */
const getS = (isMobile, isTablet) => ({
  shell: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: "'DM Sans', sans-serif",
    background: '#f0ede8',
    flexDirection: isMobile ? 'column' : 'row',
  },
  sidebar: {
    width: isMobile ? '100%' : 72,
    background: '#101F40',
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    alignItems: 'center',
    padding: isMobile ? '8px 16px' : '20px 0',
    gap: isMobile ? 4 : 8,
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: isMobile ? 'auto' : '100vh',
    zIndex: 20,
    overflowX: isMobile ? 'auto' : 'visible',
    justifyContent: isMobile ? 'space-between' : 'flex-start',
  },
  sidebarLogo: {
    width: isMobile ? 32 : 40,
    height: isMobile ? 32 : 40,
    backgroundImage: 'url(/Images/Manurevalogo.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: isMobile ? 0 : 16,
    flexShrink: 0,
  },
  sidebarIconsRow: {
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    gap: isMobile ? 4 : 8,
    alignItems: 'center',
    flex: isMobile ? 1 : 'unset',
    justifyContent: isMobile ? 'center' : 'flex-start',
  },
  sidebarIcon: (active) => ({
    width: isMobile ? 36 : 40,
    height: isMobile ? 36 : 40,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
    boxShadow: active ? 'inset 3px 0 0 #6ea8fe' : 'none',
    transition: 'background 0.2s ease, box-shadow 0.2s ease',
    flexShrink: 0,
  }),
  sidebarSpacer: { flex: 1, display: isMobile ? 'none' : 'block' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  topbar: {
    background: '#fff',
    padding: isMobile ? '0 12px' : '0 28px',
    height: isMobile ? 52 : 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e8e4df',
    position: 'sticky',
    top: isMobile ? 52 : 0,
    zIndex: 10,
  },
  companyName: {
    fontSize: isMobile ? 13 : 15,
    fontWeight: 600,
    color: '#101F40',
    letterSpacing: '-0.3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: isMobile ? 150 : 'unset',
  },
  topbarRight: { display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14 },
  searchWrap: { position: 'relative', display: isMobile ? 'none' : 'block' },
  searchInput: {
    background: '#f5f3ef',
    border: 'none',
    borderRadius: 20,
    padding: '8px 16px 8px 36px',
    fontSize: 13,
    color: '#555',
    width: isTablet ? 160 : 220,
    outline: 'none',
    fontFamily: "'DM Sans', sans-serif",
  },
  notifBtn: {
    width: isMobile ? 32 : 36,
    height: isMobile ? 32 : 36,
    borderRadius: '50%',
    background: '#f5f3ef',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flexShrink: 0,
  },
  avatarChip: {
    width: isMobile ? 32 : 36,
    height: isMobile ? 32 : 36,
    borderRadius: '50%',
    background: '#4caf4f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isMobile ? 11 : 13,
    fontWeight: 600,
    color: '#fff',
    cursor: 'pointer',
    flexShrink: 0,
  },
  powerBtn: {
    width: isMobile ? 32 : 36,
    height: isMobile ? 32 : 36,
    borderRadius: '50%',
    border: '1px solid #e8e4df',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    padding: isMobile ? 12 : isTablet ? 18 : 28,
    display: 'flex',
    flexDirection: 'column',
    gap: isMobile ? 14 : 24,
    overflowY: 'auto',
  },
  heroCard: {
    background: '#101F40',
    borderRadius: isMobile ? 14 : 20,
    padding: isMobile ? '16px' : '28px 32px',
    display: 'flex',
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? 12 : 0,
  },
  heroLeft: { display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 20, zIndex: 1, width: isMobile ? '100%' : 'auto' },
  heroAvatarWrap: {
    width: isMobile ? 52 : 64,
    height: isMobile ? 52 : 64,
    borderRadius: '50%',
    background: '#4CAF4F',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isMobile ? 18 : 22,
    color: '#fff',
    border: '3px solid rgba(255,255,255,0.2)',
    position: 'relative',
    cursor: 'pointer',
    flexShrink: 0,
    overflow: 'hidden',
  },
  heroCamBadge: {
    position: 'absolute', bottom: 0, right: 0,
    width: 20, height: 20, background: '#4f6ef7', borderRadius: '50%',
    border: '2px solid #101F40',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  heroInfoH2: { fontSize: isMobile ? 15 : 20, fontWeight: 600, color: '#fff', margin: 0, marginBottom: 6 },
  managerPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    background: 'rgba(79,110,247,0.2)', border: '1px solid rgba(79,110,247,0.4)',
    borderRadius: 20, padding: '5px 12px',
  },
  managerPillText: { fontSize: isMobile ? 11 : 12, color: '#8ba7ff' },
  managerPillStrong: { fontSize: isMobile ? 11 : 12, color: '#c4d0ff', fontWeight: 500, wordBreak: 'break-word' },
  heroRight: { zIndex: 1, textAlign: isMobile ? 'left' : 'right', paddingLeft: isMobile ? 4 : 0 },
  dateLabel: { fontSize: isMobile ? 11 : 12, color: 'rgba(255,255,255,0.4)', marginBottom: 4 },
  dateVal: { fontSize: isMobile ? 12 : 14, color: 'rgba(255,255,255,0.8)', fontWeight: 500 },
  noMgrPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.5)',
    borderRadius: 20, padding: '5px 12px',
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2,1fr)' : 'repeat(3,1fr)',
    gap: isMobile ? 10 : 16,
  },
  leaveCard: {
    background: '#fff', borderRadius: isMobile ? 12 : 16, padding: isMobile ? 14 : 20,
    border: '1px solid #e8e4df', position: 'relative', overflow: 'hidden',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  cardAccent: (color) => ({
    position: 'absolute', top: 0, left: 0, width: 4, height: '100%',
    borderRadius: '16px 0 0 16px', background: color,
  }),
  cardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: isMobile ? 10 : 14 },
  cardIcon: (bg) => ({
    width: isMobile ? 38 : 44, height: isMobile ? 38 : 44, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg,
  }),
  cardTag: (bg, color) => ({
    fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
    padding: '4px 10px', borderRadius: 20, textTransform: 'uppercase',
    background: bg, color,
  }),
  cardTitle: { fontSize: isMobile ? 12 : 13, fontWeight: 500, color: '#999', marginBottom: 4 },
  cardCount: { fontSize: isMobile ? 24 : 30, fontWeight: 600, color: '#101F40', letterSpacing: '-1px', lineHeight: 1 },
  cardCountSub: { fontSize: isMobile ? 12 : 14, fontWeight: 400, color: '#bbb', letterSpacing: 0 },
  cardMeta: { marginTop: isMobile ? 10 : 14, paddingTop: isMobile ? 10 : 14, borderTop: '1px solid #f0ede8' },
  cardBarWrap: { height: 5, background: '#f0ede8', borderRadius: 3, marginBottom: 10, overflow: 'hidden' },
  cardBar: (pct, color) => ({ height: '100%', borderRadius: 3, width: `${pct}%`, background: color, transition: 'width 0.8s ease' }),
  cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardStat: { fontSize: isMobile ? 11 : 12, color: '#aaa' },
  applyBtn: (color, disabled) => ({
    padding: isMobile ? '6px 12px' : '7px 18px', border: 'none', borderRadius: 20,
    fontSize: isMobile ? 11 : 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    background: disabled ? '#d1d5db' : color,
    color: '#fff', opacity: disabled ? 0.6 : 1,
    transition: 'opacity 0.2s', whiteSpace: 'nowrap',
  }),
  tableSection: {
    background: '#fff', borderRadius: isMobile ? 12 : 16,
    border: '1px solid #e8e4df', overflow: 'hidden',
  },
  tableHead: {
    padding: isMobile ? '12px 14px' : '18px 24px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', borderBottom: '1px solid #f0ede8',
  },
  tableTitle: { fontSize: isMobile ? 13 : 15, fontWeight: 600, color: '#101F40' },
  policyBtn: {
    padding: isMobile ? '6px 10px' : '7px 16px', background: '#f0ede8', border: 'none',
    borderRadius: 20, fontSize: isMobile ? 11 : 12, fontWeight: 500, color: '#555',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
  },
  th: {
    padding: isMobile ? '8px 10px' : '10px 24px', fontSize: isMobile ? 10 : 11, fontWeight: 600,
    color: '#aaa', letterSpacing: '0.6px', textAlign: 'left',
    background: '#faf9f7', textTransform: 'uppercase', whiteSpace: 'nowrap',
  },
  td: { padding: isMobile ? '10px 10px' : '14px 24px', fontSize: isMobile ? 12 : 13, color: '#444' },
  statusBadge: (status) => {
    const normalized = String(status || '').replace(/_/g, ' ').trim();
    const map = {
      Approved: { background: '#dcfce7', color: '#15803d' },
      Rejected: { background: '#fee2e2', color: '#b91c1c' },
      Pending:          { background: '#fef3c7', color: '#b45309' },
      'Cancel Requested':  { background: '#fff7ed', color: '#c2410c' },
      Cancelled:         { background: '#f3f4f6', color: '#6b7280' },
    };
    return {
      ...map[normalized] || map.Pending,
      display: 'inline-block',
      padding: isMobile ? '3px 8px' : '4px 12px',
      borderRadius: 20,
      fontSize: isMobile ? 10 : 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    };
  },
  leaveChip: (type) => {
    const t = (type || '').toLowerCase();
    const base = {
      display: 'inline-flex', alignItems: 'center',
      padding: isMobile ? '3px 8px' : '4px 10px',
      borderRadius: 20, fontSize: isMobile ? 10 : 12, fontWeight: 500, whiteSpace: 'nowrap',
    };
    if (t.includes('medical') || t.includes('sick')) return { ...base, background: '#ede9fe', color: '#6d28d9' };
    if (t.includes('earned') || t.includes('casual')) return { ...base, background: '#dbeafe', color: '#1d4ed8' };
    return { ...base, background: '#fce7f3', color: '#be185d' };
  },
 hintBadge: {
  display: 'inline-block',
  marginLeft: 6,
  padding: isMobile ? '3px 8px' : '4px 12px',
  borderRadius: 20,
  fontSize: isMobile ? 10 : 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  background: '#f1f5f9',
  color: '#475569',
},
  paginationWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 16, paddingBottom: 8 },
});

/* ── SVG helpers ── */
const Icon = ({ d, size = 18, stroke = 'rgba(255,255,255,0.5)', fill = 'none', sw = 1.8 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={fill} stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);


/* ─────────────────────────────────────────────
   Attendance status config (mirrors AttendanceEmployee.jsx)
───────────────────────────────────────────── */
const ATT_STATUS = {
  on_time:      { label: 'OK',          color: '#15803d', bg: '#dcfce7' },
  soft_late:    { label: 'Soft-late',   color: '#b45309', bg: '#fef3c7' },
  missed_core:  { label: 'Missed-core', color: '#b91c1c', bg: '#fee2e2' },
  short_hours:  { label: 'Short-hours', color: '#c2410c', bg: '#ffedd5' },
  needs_review: { label: 'Review',      color: '#4b5563', bg: '#e5e7eb' },
  absent:       { label: 'Absent',      color: '#c0392b', bg: '#fdeeee' },
  neutral:      { label: null,          color: '#8a8f98', bg: '#f1f2f4' },
};
const ATT_DAY_CLASS_LABEL = {
  Holiday: 'Holiday', WeekOff: 'Week-off', Leave: 'Leave',
  CompOff: 'Comp-off', HalfDay: 'Half-day', Permission: 'Permission',
};
const ATT_MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const ATT_DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const attFmtDateDMY = (iso) => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
const attDayOfWeek = (iso) => ATT_DOW[new Date(`${iso}T00:00:00`).getDay()];
const attFmtDuration = (min) => {
  if (min === null || min === undefined) return '--';
  const sign = min < 0 ? '-' : '';
  const m = Math.abs(min);
  return `${sign}${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}m`;
};

/* ─────────────────────────────────────────────
   AttendancePanel — content only (no sidebar/topbar),
   rendered inline inside Worker's Shell like NM's tab switch.
───────────────────────────────────────────── */
const AttendancePanel = ({ user, isMobile }) => {
  const now = new Date();
const year  = now.getFullYear();
const month = now.getMonth() + 1;

  const [loading,      setLoading]      = useState(true);
  const [days,         setDays]         = useState([]);
  const [noLink,       setNoLink]       = useState(false);
  const [pendingDates, setPendingDates] = useState(new Set());
  const [sortDir,      setSortDir]      = useState('desc');

  const [corrDay,        setCorrDay]        = useState(null);
  const [corrFirstIn,    setCorrFirstIn]    = useState('');
  const [corrLastOut,    setCorrLastOut]    = useState('');
  const [corrReason,     setCorrReason]     = useState('');
  const [corrError,      setCorrError]      = useState('');
  const [corrSubmitting, setCorrSubmitting] = useState(false);

  const fetchMonth = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const bu = getBackendUrl();
      const [monthRes, corrRes] = await Promise.all([
        fetch(`${bu}/api/attendance/my-month?year=${year}&month=${month}`, { credentials: 'include' }),
        fetch(`${bu}/api/attendance/my-corrections`, { credentials: 'include' }),
      ]);

      if (monthRes.ok) {
        const data = await monthRes.json();
        if (!data.employee) { setNoLink(true); setDays([]); setLoading(false); return; }
        setNoLink(false);
        setDays(data.days || []);
      }

      if (corrRes.ok) {
        const corrections = await corrRes.json();
        setPendingDates(new Set(
          (corrections || []).filter(c => c.state === 'pending').map(c => c.work_date)
        ));
      }
    } catch (e) {
      console.error('attendance fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [user, year, month]);

  useEffect(() => { fetchMonth(); }, [fetchMonth]);

  // const prevMonth = () => {
  //   if (month === 1) { setYear(y => y - 1); setMonth(12); }
  //   else setMonth(m => m - 1);
  // };
  // const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  // const nextMonth = () => {
  //   if (isCurrentMonth) return;
  //   if (month === 12) { setYear(y => y + 1); setMonth(1); }
  //   else setMonth(m => m + 1);
  // };

  const summary = useMemo(() => {
    const c = { present: 0, on_time: 0, soft_late: 0, missed_core: 0, short_hours: 0, needs_review: 0 };
    days.forEach(d => {
      if (d.status === 'absent' || d.status === 'neutral') return;
      c.present++;
      if (c[d.status] !== undefined) c[d.status]++;
    });
    return c;
  }, [days]);

  const sortedDays = useMemo(() => {
    const rows = [...days];
    rows.sort((a, b) => {
      const cmp = a.work_date < b.work_date ? -1 : a.work_date > b.work_date ? 1 : 0;
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return rows;
  }, [days, sortDir]);

  const canRequestCorrection = (row) => row.status !== 'neutral' && row.status !== 'on_time';
  // const canRequestCorrection = (row) => row.status !== 'neutral';



  const openCorrection = (row) => {
    setCorrDay(row);
    setCorrFirstIn('');
    setCorrLastOut('');
    setCorrReason('');
    setCorrError('');
  };

  const submitCorrection = async () => {
    setCorrError('');
    const reason = corrReason.trim();
    if (reason.length < 5) {
      setCorrError('Please describe what happened (at least 5 characters).');
      return;
    }
    const finalFirstIn = corrFirstIn || corrDay.first_in;
    const finalLastOut = corrLastOut || corrDay.last_out;
    if (!finalFirstIn) { setCorrError("There's no recorded In time for this day — please propose one."); return; }
    if (!finalLastOut) { setCorrError("There's no recorded Out time for this day — please propose one."); return; }

    setCorrSubmitting(true);
    try {
      const bu = getBackendUrl();
      const payload = {
        work_date: corrDay.work_date,
        corrected_first_in: `${corrDay.work_date}T${finalFirstIn}:00`,
        corrected_last_out: `${corrDay.work_date}T${finalLastOut}:00`,
        reason,
      };
      const res = await fetch(`${bu}/api/attendance/my-correction`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setPendingDates(prev => new Set(prev).add(corrDay.work_date));
        setCorrDay(null);
        Swal.fire({ icon: 'success', title: 'Sent for approval', confirmButtonColor: '#101F40', timer: 2000, showConfirmButton: false });
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 409) {
          setPendingDates(prev => new Set(prev).add(corrDay.work_date));
          setCorrError('A pending correction already exists for this date.');
        } else {
          setCorrError(err.error || 'Could not submit correction.');
        }
      }
    } catch (e) {
      setCorrError(e.message || 'Network error — please try again.');
    } finally {
      setCorrSubmitting(false);
    }
  };

  const SUMMARY_CARDS = [
    ['Present days', summary.present],
    ['On-time days', summary.on_time],
    ['Soft-late days', summary.soft_late],
    ['Missed-core days', summary.missed_core],
    ['Short-hours days', summary.short_hours],
    ['Needs-review days', summary.needs_review],
  ];

  return (
    <>
      <div style={{ minWidth: 0, flex: 1, padding: isMobile ? 12 : 28, display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20 }}>
      {/* <div style={{ flex: 1, padding: isMobile ? 12 : 28, display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20 }}>*/}
        {/* ── Hero / month selector ── */}
        <div style={{ background: '#101F40', borderRadius: isMobile ? 14 : 20, padding: isMobile ? '16px' : '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0 }}>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, background: 'rgba(79,110,247,0.15)', borderRadius: '50%' }} />
          <div style={{ zIndex: 1 }}>
            <div style={{ fontSize: isMobile ? 15 : 20, fontWeight: 600, color: '#fff', marginBottom: 4 }}>My Attendance</div>
            <div style={{ fontSize: isMobile ? 12 : 13, color: 'rgba(255,255,255,0.5)' }}>{user?.name} · {user?.email}</div>
          </div>
          <div style={{ zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: isMobile ? 13 : 15, color: 'rgba(255,255,255,0.9)', fontWeight: 600, minWidth: 120, textAlign: 'center' }}>
              {ATT_MONTHS[month - 1]} {year}
            </span>
          </div>
          {/* <div style={{ zIndex:1, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize: isMobile?13:15, color:'rgba(255,255,255,0.9)', fontWeight:600, minWidth:120, textAlign:'center' }}>
              {ATT_MONTHS[month-1]} {year}
            </span>
          </div> */}
        </div>

        {/* ── No Link Notice ── */}
        {noLink && !loading && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', border: '1px solid #e8e4df' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔗</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#101F40', marginBottom: 8 }}>Attendance Not Linked</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Your account is not linked to a biometric device. Please contact HR.</div>
          </div>
        )}

        {!noLink && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(6, minmax(0,1fr))', gap: isMobile ? 8 : 12 }}>           

              {SUMMARY_CARDS.map(([label, value]) => (
                <div key={label} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e4df', padding: isMobile ? '10px 12px' : '14px 16px' }}>
                  <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#101F40', lineHeight: 1.1 }}>{loading ? '–' : value}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* ── Daily table ── */}
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8e4df', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 12 : 13.5 }}>
                  <thead>
                    <tr style={{ background: '#fafbfc' }}>
                      <th
                        onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                        style={{ textAlign: 'left', padding: isMobile ? '10px 12px' : '12px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', borderBottom: '1px solid #e8e4df', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      >
                        Date <span style={{ opacity: 0.5 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>
                      </th>
                      {['Day', 'In', 'Out', 'Duration', 'Status', 'Action'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: isMobile ? '10px 12px' : '12px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', borderBottom: '1px solid #e8e4df', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && Array.from({ length: 6 }).map((_, i) => (
                      <tr key={`sk${i}`}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <td key={j} style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                            <div style={{ height: 12, width: j === 6 ? 90 : 46, borderRadius: 6, background: '#eef0f3' }} />
                          </td>
                        ))}
                      </tr>
                    ))}

                    {!loading && sortedDays.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: '48px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                          No attendance records for this month.
                        </td>
                      </tr>
                    )}

                    {!loading && sortedDays.map(row => {
                      const meta = ATT_STATUS[row.status] || ATT_STATUS.neutral;
                      const label = row.status === 'neutral'
                        ? (ATT_DAY_CLASS_LABEL[row.day_class] || row.day_class || 'Neutral')
                        : meta.label;
                      const isPending = pendingDates.has(row.work_date);
                      const isNeutral = row.status === 'neutral';

                      return (
                        <tr key={row.work_date} style={{ borderBottom: '1px solid #f0f2f5', opacity: isNeutral ? 0.6 : 1, fontStyle: isNeutral ? 'italic' : 'normal' }}>
                          <td style={{ padding: isMobile ? '10px 12px' : '11px 16px', whiteSpace: 'nowrap' }}>{attFmtDateDMY(row.work_date)}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '11px 16px', whiteSpace: 'nowrap' }}>{attDayOfWeek(row.work_date)}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '11px 16px', whiteSpace: 'nowrap' }}>{row.first_in || '--:--'}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '11px 16px', whiteSpace: 'nowrap' }}>{row.last_out || '--:--'}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '11px 16px', whiteSpace: 'nowrap' }}>{attFmtDuration(row.presence_min)}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '11px 16px' }}>
                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: meta.bg, color: meta.color, whiteSpace: 'nowrap' }}>{label}</span>
                          </td>
                          <td style={{ padding: isMobile ? '10px 12px' : '11px 16px', whiteSpace: 'nowrap' }}>
                            {isPending ? (
                              <button disabled style={{ border: '1px solid #e8e4df', color: '#9ca3af', background: '#f9fafb', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'default' }}>
                                Correction pending
                              </button>
                            ) : canRequestCorrection(row) ? (
                              <button
                                onClick={() => openCorrection(row)}
                                style={{ border: '1px solid #101F40', color: '#101F40', background: 'transparent', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                              >
                                Request correction
                              </button>
                            ) : (
                              <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Correction Modal ── */}
      {corrDay && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.6)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(4px)', padding: isMobile ? 0 : 20, fontFamily: "'DM Sans',sans-serif" }}>
          <div style={{ background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : 20, width: '100%', maxWidth: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#101F40' }}>Request correction — {attFmtDateDMY(corrDay.work_date)} ({attDayOfWeek(corrDay.work_date)})</span>
              <button onClick={() => setCorrDay(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#aaa' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current In</label>
                  <input type="text" value={corrDay.first_in || '--:--'} disabled
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e8e4df', borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans',sans-serif", background: '#f9fafb', color: '#6b7280', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Out</label>
                  <input type="text" value={corrDay.last_out || '--:--'} disabled
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e8e4df', borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans',sans-serif", background: '#f9fafb', color: '#6b7280', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Proposed In</label>                 
                  <TimePicker value={corrFirstIn} onChange={setCorrFirstIn} placeholder="Keep current" />
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Leave blank to keep the current In time</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Proposed Out</label>                  
                  <TimePicker value={corrLastOut} onChange={setCorrLastOut} placeholder="Keep current" align="right" />
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Leave blank to keep the current Out time</div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reason *</label>
                <textarea
                  value={corrReason}
                  onChange={e => setCorrReason(e.target.value)}
                  placeholder="Explain why the punch is missing or incorrect…"
                  maxLength={300}
                  style={{ width: '100%', minHeight: 80, padding: '10px 12px', border: '1px solid #e8e4df', borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: 'none', resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, textAlign: 'right' }}>{corrReason.length}/300</div>
              </div>

              {corrError && (
                <div style={{ fontSize: 12.5, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                  {corrError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={submitCorrection}
                  disabled={corrSubmitting}
                  style={{ flex: 1, padding: 12, background: corrSubmitting ? '#d1d5db' : '#101F40', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: corrSubmitting ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}
                >
                  {corrSubmitting ? 'Submitting…' : 'Submit'}
                </button>
                <button
                  onClick={() => setCorrDay(null)}
                  style={{ padding: '12px 20px', background: '#fff', color: '#6b7280', border: '1px solid #e8e4df', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


const CAL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CAL_DOW = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const calISO = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const calTodayISO = () => { const n = new Date(); return calISO(n.getFullYear(), n.getMonth(), n.getDate()); };
const calFmtLong = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

const LeaveDatePicker = ({ value, onChange, min, disabledMap, disablePast = true, placeholder = 'dd-mm-yyyy', accent = '#4f6ef7' }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const today = calTodayISO();
  const seed = value || (min && min > today ? min : today);
  const [viewY, setViewY] = useState(() => Number(seed.slice(0, 4)));
  const [viewM, setViewM] = useState(() => Number(seed.slice(5, 7)) - 1);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = () => {
    if (!open && value) { setViewY(Number(value.slice(0, 4))); setViewM(Number(value.slice(5, 7)) - 1); }
    setOpen(o => !o);
  };
  const shift = (delta) => {
    let m = viewM + delta, y = viewY;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    setViewM(m); setViewY(y);
  };

  const firstDow = new Date(viewY, viewM, 1).getDay();
  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const reasonFor = (iso) => {
    if (disablePast && iso < today) return 'Past date';
    if (min && iso < min) return 'Before start date';
    if (disabledMap && disabledMap.has(iso)) return disabledMap.get(iso) || 'Holiday / Week-off';
    return null;
  };

  const pick = (d) => {
    const iso = calISO(viewY, viewM, d);
    if (reasonFor(iso)) return;
    onChange(iso);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'absolute' }}>
      <div onClick={toggle} style={{ width: '100%', padding: '9px 12px', border: `1px solid ${value ? accent : '#e8e4df'}`, borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ color: value ? '#101F40' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value ? calFmtLong(value) : placeholder}</span>
        <span style={{ width: 24, height: 24, borderRadius: 6, background: '#eef1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke={accent} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 6, border: '1px solid #e8e4df', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.12)', padding: 12, background: '#fff', fontFamily: "'DM Sans', sans-serif" }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button type="button" onClick={() => shift(-1)} style={{ border: 'none', background: '#f5f3ef', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontSize: 16, color: '#555' }}>‹</button>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#101F40' }}>{CAL_MONTHS[viewM]} {viewY}</div>
            <button type="button" onClick={() => shift(1)} style={{ border: 'none', background: '#f5f3ef', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontSize: 16, color: '#555' }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
            {CAL_DOW.map((d, i) => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: (i === 0 || i === 6) ? '#ef4444' : '#6b7280', padding: '4px 0' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d == null) return <div key={`e${i}`} />;
              const iso = calISO(viewY, viewM, d);
              const reason = reasonFor(iso);
              const disabled = !!reason;
              const isSel = value === iso;
              const isToday = iso === today;
              const isHoliday = disabledMap && disabledMap.has(iso);
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={disabled}
                  onClick={() => pick(d)}
                  title={reason || ''}
                  style={{
                    border: 'none', borderRadius: 8, padding: 'px 0', fontSize: 13,
                    cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif",
                    background: isSel ? accent : (isHoliday ? '#fdeeee' : 'transparent'),
                    color: isSel ? '#fff' : disabled ? (isHoliday ? '#e79a9a' : '#d1d5db') : isToday ? accent : '#374151',
                    fontWeight: isSel || isToday ? 700 : 400,
                    textDecoration: isHoliday && !isSel ? 'line-through' : 'none',
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
       
        </div>
      )}
    </div>
  );
};

const Worker = () => {
  /* ── responsive ── */
  const windowWidth = useWindowSize();
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const S = getS(isMobile, isTablet);

  /* ── helpers ── */
  const parseDDMMYYYY = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    dateStr = dateStr.trim();
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) return new Date(year, month, day);
    }
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const { user, handleLogout, checkAuthStatus, managerId, managerName, managerEmail } = useAuth();
  const navigate = useNavigate();
  const recentRequestRef = useRef(null);
  const [activeView, setActiveView] = useState('overview'); // 'overview' | 'attendance'
  /* ── state ── */
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const DefaultProfile = '/Images/Manurevalogo.png';
  const [profilePicture, setProfilePicture] = useState(DefaultProfile);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const profileFileInputRef = useRef(null);
  const filePickerOpenRef = useRef(false);
  const [managerInfo, setManagerInfo] = useState({ managerId: null, managerName: null, managerEmail: null, hasAssignedManager: false, canApplyLeave: true, isLoading: true });
  const DEFAULT_LIMITS = { CASUAL: 15, MEDICAL: 0, PERMISSION: 2, PERMISSION_HOURS_PER_APPLICATION: 2 };
  const [leaveLimits, setLeaveLimits] = useState(() => {
    try {
      const savedConfig = localStorage.getItem('leaveConfiguration');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        if (config.policies) {
          return {
            CASUAL: config.policies.casual?.maxDaysPerYear || DEFAULT_LIMITS.CASUAL,
            MEDICAL: config.policies.sick?.maxDaysPerYear || DEFAULT_LIMITS.MEDICAL,
            PERMISSION: config.policies.emergency?.maxApplicationsPerMonth ?? DEFAULT_LIMITS.PERMISSION,
            PERMISSION_HOURS_PER_APPLICATION: config.policies.emergency?.hoursPerApplication || DEFAULT_LIMITS.PERMISSION_HOURS_PER_APPLICATION,
          };
        }
      }
    } catch { }
    return DEFAULT_LIMITS;
  });
  const [permissionApplicationsUsed, setPermissionApplicationsUsed] = useState(0);
  const [permissionApplicationsPending, setPermissionApplicationsPending] = useState(0);
  const [leaveSummary, setLeaveSummary] = useState({ casual: 0, medical: 0, permission: 0 });
  const [pendingLeaveSummary, setPendingLeaveSummary] = useState({ casual: 0, medical: 0, permission: 0 });
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [actualLeaveBalance, setActualLeaveBalance] = useState({
    casual: { available: 0, allocated: 2, used: 0, pending: 0, carry_forward: 0, extra_leave: 0, lop_days: 0, is_lop: false },
    medical: { available: 0, allocated: 0, used: 0, carry_forward: 0 },
    permission: { available: 0, allocated: 2, used: 0, carry_forward: 0 },
  });
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [requestsError, setRequestsError] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshIntervalRef = useRef(null);
  const lastFetchedDataRef = useRef(null);
  const notificationIntervalRef = useRef(null);
  const [pendingLeaveUpdates, setPendingLeaveUpdates] = useState([]);
  const pendingUpdatesRef = useRef([]);
  useEffect(() => { pendingUpdatesRef.current = pendingLeaveUpdates; }, [pendingLeaveUpdates]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // ✅ NEW: Permission date state
  const [permissionDate, setPermissionDate] = useState('');
  const [showPermissionCal, setShowPermissionCal] = useState(false);
  const [permissionCalMonth, setPermissionCalMonth] = useState(new Date());

  // ── Calendar helpers ────────────────────────────────────────────────────
  const [holidays, setHolidays] = React.useState([]);
  const [showFromCal, setShowFromCal] = React.useState(false);
  const [showToCal,   setShowToCal]   = React.useState(false);
  const [fromCalMonth, setFromCalMonth] = React.useState(new Date());
  const [toCalMonth,   setToCalMonth]   = React.useState(new Date());

  React.useEffect(() => {
    (async () => {
      try {
        const bu = getBackendUrl();
        const r = await fetch(`${bu}/api/holidays`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setHolidays((d.holidays || d || []).map(h => (h.date || h).slice(0, 10)));
        }
      } catch (e) { console.warn('holidays', e); }
    })();
  }, []);

  const _toLocalIso = (obj) => {
    const y = obj.getFullYear();
    const m = String(obj.getMonth()+1).padStart(2,'0');
    const d = String(obj.getDate()).padStart(2,'0');
    return `${y}-${m}-${d}`;
  };
  const _wkIsDisabled = (obj) => {
    const day = obj.getDay();
    if (day === 0 || day === 6) return true;
    const iso = _toLocalIso(obj);
    return holidays.includes(iso);
  };

  const _wkRenderCal = (calMo, setCalMo, sel, onPick, onClose) => {
    const yr = calMo.getFullYear(), mo = calMo.getMonth();
    const fd = new Date(yr, mo, 1).getDay();
    const dim = new Date(yr, mo + 1, 0).getDate();
    const todayIso = _toLocalIso(new Date());
    const MN = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
    const cells = [...Array(fd).fill(null), ...Array.from({length:dim},(_,i)=>i+1)];
    return (
      <div style={{position:'absolute',zIndex:9999,background:'#fff',
        border:'1px solid #e8e4df',borderRadius:12,
        boxShadow:'0 8px 32px rgba(0,0,0,0.18)',padding:16,
        width:272,top:'100%',left:0,marginTop:4}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
          <button type="button" onClick={()=>setCalMo(new Date(yr,mo-1,1))}
            style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#6b7280',lineHeight:1,padding:'0 4px'}}>&#8249;</button>
          <span style={{fontWeight:600,fontSize:14,color:'#111827'}}>{MN[mo]}, {yr}</span>
          <button type="button" onClick={()=>setCalMo(new Date(yr,mo+1,1))}
            style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#6b7280',lineHeight:1,padding:'0 4px'}}>&#8250;</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,marginBottom:4}}>
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d=>(
            <div key={d} style={{textAlign:'center',fontSize:11,fontWeight:600,padding:'3px 0',
              color:d==='Su'||d==='Sa'?'#ef4444':'#9ca3af'}}>{d}</div>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
          {cells.map((d,i)=>{
            if(!d) return <div key={i}/>;
            const obj2=new Date(yr,mo,d);
            const iso=_toLocalIso(obj2);
            const dis=_wkIsDisabled(obj2);
            const isSel=iso===sel, isHol=holidays.includes(iso);
            const isWE=obj2.getDay()===0||obj2.getDay()===6;
            const isToday=iso===todayIso;
            return (
              <button type="button" key={i} disabled={dis}
                onClick={()=>{if(!dis){onPick(iso);onClose();}}}
                title={isHol?'Government holiday':isWE?'Weekend':''}
                style={{padding:'6px 0',border:'none',borderRadius:5,
                  cursor:dis?'not-allowed':'pointer',fontSize:13,textAlign:'center',
                  background:isSel?'#2563eb':isToday?'#eff6ff':'transparent',
                  color:dis?'#d1d5db':isSel?'#fff':(isWE||isHol)?'#ef4444':'#111827',
                  fontWeight:isToday?700:400,
                  textDecoration:isHol&&!isSel?'line-through':'none'}}>
                {d}
              </button>
            );
          })}
        </div>
        <div style={{marginTop:10,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontSize:11,color:'#9ca3af'}}>
            <span style={{color:'#ef4444'}}>&#9679;</span> Weekend/Holiday
          </span>
          <button type="button" onClick={onClose}
            style={{fontSize:12,color:'#6b7280',background:'none',border:'none',cursor:'pointer'}}>Close</button>
        </div>
      </div>
    );
  };
  // ── end calendar helpers ─────────────────────────────────────────────────

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [topMessage, setTopMessage] = useState('');
  const [showTopMessage, setShowTopMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState(null);
  const [showLeavePolicyModal, setShowLeavePolicyModal] = useState(false);
  const [cardHover, setCardHover] = useState(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationPanelRef = useRef(null);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  /* ── att_calendar disabled dates (holidays + week-offs) ── */
  const [disabledDatesMap, setDisabledDatesMap] = useState(new Map()); // 'YYYY-MM-DD' -> label

  /* ── today's date string ── */
  const todayStr = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const todayStrShort = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  /* ── user initials ── */
  const getInitials = () => {
    if (user?.name?.trim()) return user.name.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  const normalizeStatusText = (status) => {
    const normalized = String(status || '').replace(/_/g, ' ').trim();
    if (!normalized) return 'Pending';
    return normalized
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const handleRemoveProfilePicture = async () => {
    if (isUploadingProfile) return;
    try {
      setIsUploadingProfile(true);
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/user/profile-picture`, { method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (response.ok) {
        setProfilePicture(DefaultProfile); setPreviewImage(null);
        const cacheKey = `profile_picture_${user.email}`; localStorage.removeItem(cacheKey);
        setTopMessage('Profile picture removed! Using default logo.'); setShowTopMessage(true); setTimeout(() => setShowTopMessage(false), 3000);
      } else { throw new Error('Failed to remove profile picture'); }
    } catch (error) {
      setTopMessage('Failed to remove profile picture. Please try again.'); setShowTopMessage(true); setTimeout(() => setShowTopMessage(false), 3000);
    } finally { setIsUploadingProfile(false); }
  };

  const handleNotificationIconClick = () => { recentRequestRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const fetchProfilePicture = useCallback(async (forceRefresh = false) => {
    if (!user) return;
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/user/profile-picture`, {
        method: 'GET', credentials: 'include',
        headers: { Accept: 'application/json' }, cache: 'no-cache'
      });
      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture || DefaultProfile);
      }
    } catch { }
  }, [user]);

  const refreshProfilePicture = useCallback(async () => { if (!user) return; await fetchProfilePicture(); }, [user, fetchProfilePicture]);

  // useEffect(() => {
  //   const fetchLeavePolicy = async () => {
  //     try {
  //       const backendUrl = getBackendUrl();
  //       const response = await fetch(`${backendUrl}/api/settings/leave-policy`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
  //       if (response.ok) {
  //         const data = await response.json();
  //         const newLimits = { CASUAL: parseInt(data.casualLeave) || 0, MEDICAL: parseInt(data.medicalLeave) || 0, PERMISSION: parseInt(data.permissionRequests) || 0, PERMISSION_HOURS_PER_APPLICATION: 2 };
  //         setLeaveLimits(newLimits);
  //         setActualLeaveBalance(prev => ({ ...prev, casual: { ...prev.casual, allocated: newLimits.CASUAL }, medical: { ...prev.medical, allocated: newLimits.MEDICAL }, permission: { ...prev.permission, allocated: newLimits.PERMISSION } }));
  //       }
  //     } catch { }
  //   };
  //   fetchLeavePolicy();
  // }, []);

  useEffect(() => {
    if (user?.email) {
      const timer = setTimeout(() => fetchProfilePicture(), 100);
      return () => { clearTimeout(timer); setPreviewImage(null); setShowProfileModal(false); };
    }
    return () => { setPreviewImage(null); setShowProfileModal(false); };
  }, [user, fetchProfilePicture]);

  useEffect(() => {
    const handleFocus = () => { if (user?.email && !previewImage) refreshProfilePicture(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, refreshProfilePicture, previewImage]);

  useEffect(() => {
    if (user?.email) {
      const interval = setInterval(() => { if (!previewImage) refreshProfilePicture(); }, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, refreshProfilePicture, previewImage]);

  const handleProfileClick = () => setShowProfileModal(true);

 const handleProfileFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    event.target.value = null;
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Invalid file type</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Please select an image file (JPG, PNG, GIF, etc.)</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    event.target.value = null;
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">File too large</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">File size must be less than <strong>5MB</strong>. Please choose a smaller image.</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
    return;
  }

  const reader = new FileReader();
  reader.onloadend = () => {
    setPreviewImage(reader.result);
    setTimeout(() => { filePickerOpenRef.current = false; }, 300);
  };
  reader.readAsDataURL(file);
  event.target.value = null;
};

const handleUploadProfilePicture = async () => {
  if (!previewImage) return;
  setIsUploadingProfile(true);
  try {
    const backendUrl = getBackendUrl();
    const response = await fetch(`${backendUrl}/api/user/profile-picture`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile_picture: previewImage })
    });

    if (response.ok) {
      if (user?.email) localStorage.removeItem(`profile_picture_${user.email}`);
      localStorage.removeItem('topbar_profile_pic');
      setProfilePicture(previewImage);
      setShowProfileModal(false);
      setPreviewImage(null);
      showTopSuccessMessage('Profile picture updated successfully!');
    } else {
      Swal.fire({
        html: `
          <div style="padding: 1rem 0.5rem">
            <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Upload Failed</p>
            <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Failed to upload profile picture. Please try again.</p>
          </div>
        `,
        width: '340px',
        confirmButtonText: 'Close',
        confirmButtonColor: '#111111',
        customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
      });
    }
  } catch (err) {
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Upload Failed</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${err.message || 'Something went wrong. Please try again.'}</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
  } finally {
    setIsUploadingProfile(false);
  }
};

const handleUseEmailAvatar = async () => {
  setIsUploadingProfile(true);
  try {
    const backendUrl = getBackendUrl();
    const response = await fetch(`${backendUrl}/api/user/profile-picture`, { method: 'DELETE', credentials: 'include' });
    if (response.ok) {
      const data = await response.json();
      if (user?.email) localStorage.removeItem(`profile_picture_${user.email}`);
      setProfilePicture(data.profile_picture);
      setShowProfileModal(false);
      setPreviewImage(null);
      showTopSuccessMessage('Switched to email avatar successfully!');
    }
  } catch (err) {
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Switch Failed</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${err.message || 'Failed to switch to email avatar. Please try again.'}</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
  } finally {
    setIsUploadingProfile(false);
  }
};

  const closeProfileModal = () => {
    if (filePickerOpenRef.current) return;
    setShowProfileModal(false);
    setPreviewImage(null);
  };
  const showMessage = (text, type = 'info') => { setMessage(text); setMessageType(type); if (type === 'success') setTimeout(() => { setMessage(''); setMessageType(''); }, 5000); };
  const showTopSuccessMessage = (text) => { setTopMessage(text); setShowTopMessage(true); setTimeout(() => { setShowTopMessage(false); setTopMessage(''); }, 5000); };
  const clearMessage = () => { setMessage(''); setMessageType(''); };
  // Clear a validation error as soon as the user edits the form
  useEffect(() => {
    if (messageType === 'error') clearMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, permissionDate, reason, isHalfDay, halfDayPeriod, selectedFile, selectedLeaveType]);
  
  const fetchNotificationCount = useCallback(async () => {
    if (!user) return;
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/notifications`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const notifications = await response.json();
        if (!Array.isArray(notifications)) { setNotificationCount(0); return; }
        const unreadCount = notifications.filter(n => !n.isRead).length;
        setNotificationCount(prev => prev !== unreadCount ? unreadCount : prev);
      }
    } catch { }
  }, [user]);

  const handleNotificationClick = async () => {
    if (showNotificationPanel) {
      setShowNotificationPanel(false);
      return;
    }
    try {
      const backendUrl = getBackendUrl();
      const notificationsResponse = await fetch(`${backendUrl}/api/notifications`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      });
      if (notificationsResponse.ok) {
        const data = await notificationsResponse.json();
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch { }
    setShowNotificationPanel(true);
    if (notificationCount > 0) {
      try {
        const response = await fetch(`${getBackendUrl()}/api/notifications/mark-read`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        });
        if (response.ok) {
          setNotificationCount(0);
          if (notificationIntervalRef.current) clearInterval(notificationIntervalRef.current);
          setTimeout(() => {
            if (user) notificationIntervalRef.current = setInterval(() => fetchNotificationCount(), 30000);
          }, 5000);
        }
      } catch { }
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotificationCount();
      notificationIntervalRef.current = setInterval(() => fetchNotificationCount(), 30000);
      return () => { if (notificationIntervalRef.current) clearInterval(notificationIntervalRef.current); };
    }
  }, [user, fetchNotificationCount]);

  const fetchManagerInfo = useCallback(async () => {
    if (!user) return;
    setManagerInfo(prev => ({ ...prev, isLoading: true }));
    try {
      const backendUrl = getBackendUrl();
      try {
        const response = await fetch(`${backendUrl}/api/employee/manager-info`, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } });
        if (response.ok) {
          const data = await response.json();
          if (data.manager_id || data.managerId) { setManagerInfo({ managerId: data.manager_id || data.managerId, managerName: data.manager_name || data.managerName, managerEmail: data.manager_email || data.managerEmail, hasAssignedManager: true, canApplyLeave: true, isLoading: false }); return; }
        }
      } catch { }
      if (managerId || managerName) { setManagerInfo({ managerId, managerName, managerEmail, hasAssignedManager: true, canApplyLeave: true, isLoading: false }); return; }
      setManagerInfo({ managerId: null, managerName: null, managerEmail: null, hasAssignedManager: false, canApplyLeave: false, isLoading: false });
    } catch { setManagerInfo({ managerId: null, managerName: null, managerEmail: null, hasAssignedManager: false, canApplyLeave: false, isLoading: false }); }
  }, [user, managerId, managerName, managerEmail]);

  const validateFile = (file) => {
    if (!file) return { isValid: false, error: '' };
    const validTypes = ['application/pdf'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(file.type) && fileExtension !== '.pdf') return { isValid: false, error: 'Please select only PDF files' };
    if (file.size > 5 * 1024 * 1024) return { isValid: false, error: 'File size must be less than 5MB' };
    if (file.size === 0) return { isValid: false, error: 'File appears to be empty' };
    if (file.size < 100) return { isValid: false, error: 'File appears to be too small or corrupted' };
    return { isValid: true, error: '' };
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setFileError(''); setSelectedFile(null);
    if (!file) return;
    const validation = validateFile(file);
    if (!validation.isValid) { setFileError(validation.error); event.target.value = ''; return; }
    try {
      const reader = new FileReader();
      reader.onload = (e) => { if (e.target.result?.byteLength > 0) { setSelectedFile(file); setFileError(''); } else { setFileError('Unable to read the selected file'); event.target.value = ''; } };
      reader.onerror = () => { setFileError('Error reading the selected file'); event.target.value = ''; };
      reader.readAsArrayBuffer(file.slice(0, 1024));
    } catch { setFileError('Invalid file selected'); event.target.value = ''; }
  };

  const removeFile = () => { setSelectedFile(null); setFileError(''); const fi = document.getElementById('pdf-upload'); if (fi) fi.value = ''; };

  const fetchIndividualLeaveBalance = useCallback(async () => {
    if (!user?.id) return;
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/employee/${user.id}/leave-balance`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (response.ok) {
        const data = await response.json();
        if (data.balances) {
          const individualLimits = { CASUAL: data.balances.casual?.allocated || DEFAULT_LIMITS.CASUAL, MEDICAL: data.balances.medical?.allocated || DEFAULT_LIMITS.MEDICAL, PERMISSION: data.balances.permission?.allocated ?? DEFAULT_LIMITS.PERMISSION, PERMISSION_HOURS_PER_APPLICATION: leaveLimits.PERMISSION_HOURS_PER_APPLICATION };
          setLeaveLimits(individualLimits);
          setActualLeaveBalance({
            casual: { available: (Number(data.balances.casual?.allocated) || 0) - (Number(data.balances.casual?.used) || 0), allocated: Number(data.balances.casual?.allocated_days) || 0, used: Number(data.balances.casual?.used) || 0, pending: Number(data.balances.casual?.pending) || 0, carry_forward: Number(data.balances.casual?.carry_forward_from_previous_month) || 0, extra_leave: Number(data.balances.casual?.extra_leave_days) || 0, lop_days: Number(data.balances.casual?.lop_days) || 0, is_lop: Boolean(data.balances.casual?.is_lop) },
            medical: { available: Number(data.balances.medical?.remaining) || 0, allocated: Number(data.balances.medical?.allocated) || 0, used: Number(data.balances.medical?.used) || 0, carry_forward: Number(data.balances.medical?.carry_forward_days) || 0 },
            permission: { available: Number(data.balances.permission?.remaining) || 0, allocated: Number(data.balances.permission?.allocated ?? 0), used: Number(data.balances.permission?.used) || 0, carry_forward: 0 },
          });
        }
      } else { setLeaveLimits(DEFAULT_LIMITS); }
    } catch { setLeaveLimits(DEFAULT_LIMITS); }
  }, [user?.id, leaveLimits.PERMISSION_HOURS_PER_APPLICATION]);

  useEffect(() => {
    fetchIndividualLeaveBalance();
    const handleBalanceAdjusted = (event) => {
      if (event.detail?.employeeId === 'all' && event.detail?.updateType === 'global_policy_update') {
        try {
          const savedConfig = localStorage.getItem('leaveConfiguration');
          if (savedConfig) {
            const config = JSON.parse(savedConfig);
            if (config.policies) {
              const updatedLimits = { CASUAL: config.policies.casual?.maxDaysPerYear || DEFAULT_LIMITS.CASUAL, MEDICAL: config.policies.sick?.maxDaysPerYear || DEFAULT_LIMITS.MEDICAL, PERMISSION: config.policies.emergency?.maxApplicationsPerMonth ?? DEFAULT_LIMITS.PERMISSION, PERMISSION_HOURS_PER_APPLICATION: config.policies.emergency?.hoursPerApplication || DEFAULT_LIMITS.PERMISSION_HOURS_PER_APPLICATION };
              setLeaveLimits(updatedLimits);
              setActualLeaveBalance(prev => ({ ...prev, casual: { ...prev.casual, allocated: updatedLimits.CASUAL }, medical: { ...prev.medical, allocated: updatedLimits.MEDICAL }, permission: { ...prev.permission, allocated: updatedLimits.PERMISSION } }));
            }
          }
        } catch { }
        fetchIndividualLeaveBalance();
      } else if (event.detail?.employeeId === user?.id) { fetchIndividualLeaveBalance(); }
    };
    window.addEventListener('leaveBalanceAdjusted', handleBalanceAdjusted);
    return () => window.removeEventListener('leaveBalanceAdjusted', handleBalanceAdjusted);
  }, [user?.id]);

  useEffect(() => { checkAuthStatus(); }, [checkAuthStatus]);
  useEffect(() => { if (user) fetchManagerInfo(); }, [fetchManagerInfo, user]);

  const fetchLeaveSummary = useCallback(async () => {
    if (!user) { setIsLoadingSummary(false); return; }
    setIsLoadingSummary(true); setSummaryError(null);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/dashboard/leave-summary`, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } });
      if (response.ok) { const data = await response.json(); setLeaveSummary({ casual: Number(data.casual) || 0, medical: Number(data.medical) || 0, permission: Number(data.permission) || 0 }); setSummaryError(null); } else { throw new Error(`Server error: ${response.status}`); }
    } catch { setSummaryError('Unable to load leave balance'); setLeaveSummary({ casual: 0, medical: 0, permission: 0 }); }
    finally { setIsLoadingSummary(false); }
  }, [user]);

  const calculatePermissionApplications = useCallback((requests) => {
    const now = new Date(); const cm = now.getMonth(); const cy = now.getFullYear();
    let approvedCount = 0; let pendingCount = 0;
    requests.forEach(r => {
      const lt = (r.leaveType || r.leave_type || '').toLowerCase();
      if (lt.includes('permission') || lt.includes('emergency')) {
        try {
          const rd = new Date(r.createdAt || r.created_at);
          if (rd.getMonth() === cm && rd.getFullYear() === cy) {
            const s = String(r.status || '').replace(/_/g, ' ').trim().toLowerCase();
            if (s === 'approved' || s === 'cancel requested') approvedCount++;
            else if (s === 'pending') pendingCount++;
          }
        } catch { }
      }
    });
    return { approvedCount, pendingCount };
  }, []);

  const calculatePendingCounts = useCallback((requests) => {
    const pendingCounts = { casual: 0, medical: 0, permission: 0 };
    requests.forEach(r => {
      const rStatus = String(r.status || '').replace(/_/g, ' ').trim().toLowerCase();
      if (rStatus === 'pending') {
        const lt = (r.leaveType || r.leave_type || '').toLowerCase();
        const days = r.daysRequested || r.days_requested || 1;
        if (lt.includes('casual')) pendingCounts.casual += days;
        else if (lt.includes('medical') || lt.includes('sick')) pendingCounts.medical += days;
        else if (lt.includes('permission') || lt.includes('emergency')) pendingCounts.permission += days;
      }
    });
    return pendingCounts;
  }, []);

  const fetchLeaveRequests = useCallback(async () => {
    if (!user) { setIsLoadingRequests(false); return; }
    setIsLoadingRequests(true); setRequestsError(null);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/leave-history/user-requests`, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setLeaveRequests(data); setRequestsError(null);
          const calculatedPendingCounts = calculatePendingCounts(data);
          setPendingLeaveSummary(calculatedPendingCounts);
          const { approvedCount, pendingCount } = calculatePermissionApplications(data);
          setPermissionApplicationsUsed(approvedCount); setPermissionApplicationsPending(pendingCount);
          checkPendingStatusChanges(data);
        } else { setLeaveRequests([]); }
      } else { throw new Error(`Server error: ${response.status}`); }
    } catch { setRequestsError('Unable to load leave requests'); setLeaveRequests([]); }
    finally { setIsLoadingRequests(false); }
  }, [user, calculatePendingCounts, calculatePermissionApplications]);

  const checkPendingStatusChanges = useCallback((newRequests) => {
    const currentPendingUpdates = pendingUpdatesRef.current;
    if (currentPendingUpdates.length === 0) return;
    const updatesToRemove = []; const updatesToRevert = [];
    currentPendingUpdates.forEach(pendingUpdate => {
      let matchingRequest = null;
      if (pendingUpdate.requestId && typeof pendingUpdate.requestId === 'string' && !pendingUpdate.requestId.startsWith('temp_')) matchingRequest = newRequests.find(req => req.id === pendingUpdate.requestId);
      if (!matchingRequest) matchingRequest = newRequests.find(req => { const rtl = (req.leaveType || '').toLowerCase(); const ptl = (pendingUpdate.leaveType || '').toLowerCase(); return (rtl.includes(ptl) || ptl.includes(rtl)) && req.dateApplied && pendingUpdate.dateApplied && new Date(req.dateApplied).toDateString() === new Date(pendingUpdate.dateApplied).toDateString(); });
      if (matchingRequest) {
        const status = (matchingRequest.status || '').toLowerCase();
        if (status === 'rejected') { updatesToRevert.push({ ...pendingUpdate, action: 'reject' }); updatesToRemove.push(pendingUpdate.tempId || pendingUpdate.requestId); if (pendingUpdate.summaryKey === 'permission') setPermissionApplicationsPending(prev => Math.max(0, prev - 1)); }
        else if (status === 'approved') { updatesToRemove.push(pendingUpdate.tempId || pendingUpdate.requestId); setPendingLeaveSummary(prev => ({ ...prev, [pendingUpdate.summaryKey]: Math.max(0, prev[pendingUpdate.summaryKey] - pendingUpdate.days) })); setLeaveSummary(prev => ({ ...prev, [pendingUpdate.summaryKey]: prev[pendingUpdate.summaryKey] + pendingUpdate.days })); if (pendingUpdate.summaryKey === 'permission') { setPermissionApplicationsPending(prev => Math.max(0, prev - 1)); setPermissionApplicationsUsed(prev => prev + 1); } }
      }
    });
    if (updatesToRevert.length > 0) {
      setPendingLeaveSummary(prev => { const updated = { ...prev }; updatesToRevert.forEach(u => { if (u.action === 'reject') updated[u.summaryKey] = Math.max(0, updated[u.summaryKey] - u.days); }); return updated; });
      setTimeout(() => { const rj = updatesToRevert.filter(u => u.action === 'reject'); if (rj.length === 1) showTopSuccessMessage(`Leave request rejected. ${rj[0].days} ${rj[0].summaryKey} ${rj[0].summaryKey === 'permission' ? 'hour(s)' : 'day(s)'} removed.`); else if (rj.length > 1) showTopSuccessMessage(`${rj.length} leave requests were rejected.`); }, 100);
    }
    if (updatesToRemove.length > 0) setPendingLeaveUpdates(prev => prev.filter(p => !updatesToRemove.includes(p.tempId) && !updatesToRemove.includes(p.requestId)));
  }, []);

  const forceRefreshData = useCallback(async () => {
    const now = Date.now();
    if (forceRefreshData.lastCall && now - forceRefreshData.lastCall < 5000) return;
    forceRefreshData.lastCall = now;
    setIsRefreshing(true); setSummaryError(null); setRequestsError(null);
    try { await Promise.all([fetchIndividualLeaveBalance(), fetchLeaveSummary(), fetchLeaveRequests()]); setLastRefresh(Date.now()); }
    catch { } finally { setIsRefreshing(false); }
  }, [fetchIndividualLeaveBalance, fetchLeaveSummary, fetchLeaveRequests]);

  const hasDataChanged = useCallback((newData, oldData) => {
    if (!oldData) return true;
    try {
      if (newData.summary && oldData.summary) { if (newData.summary.casual !== oldData.summary.casual || newData.summary.medical !== oldData.summary.medical || newData.summary.permission !== oldData.summary.permission) return true; }
      if (newData.requests && oldData.requests) { if (newData.requests.length !== oldData.requests.length) return true; for (let i = 0; i < newData.requests.length; i++) { const nr = newData.requests[i]; const or = oldData.requests.find(r => r.id === nr.id); if (or && or.status !== nr.status) return true; } }
      return false;
    } catch { return true; }
  }, []);

  useEffect(() => {
    if (!user) return;
    let intervalId = null; let lastRequestTime = 0;
    const startAutoRefresh = async () => {
      await forceRefreshData(); lastRequestTime = Date.now();
      intervalId = setInterval(async () => {
        const now = Date.now();
        if (now - lastRequestTime < 10000) return;
        try {
          lastRequestTime = now;
          const backendUrl = getBackendUrl();
          const [sr, rr] = await Promise.all([fetch(`${backendUrl}/api/dashboard/leave-summary`, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } }), fetch(`${backendUrl}/api/leave-history/user-requests`, { method: 'GET', credentials: 'include', headers: { Accept: 'application/json' } })]);
          if (sr.ok && rr.ok) {
            const [sd, rd] = await Promise.all([sr.json(), rr.json()]);
            const newData = { summary: { casual: Number(sd.casual) || 0, medical: Number(sd.medical) || 0, permission: Number(sd.permission) || 0 }, requests: Array.isArray(rd) ? rd : [] };
            if (hasDataChanged(newData, lastFetchedDataRef.current)) { setLeaveSummary(newData.summary); setLeaveRequests(newData.requests); setPendingLeaveSummary(calculatePendingCounts(newData.requests)); const { approvedCount, pendingCount } = calculatePermissionApplications(newData.requests); setPermissionApplicationsUsed(approvedCount); setPermissionApplicationsPending(pendingCount); checkPendingStatusChanges(newData.requests); setLastRefresh(Date.now()); }
            lastFetchedDataRef.current = newData;
          }
        } catch { }
      }, 30000);
    };
    startAutoRefresh();
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [user, forceRefreshData, hasDataChanged, checkPendingStatusChanges, calculatePendingCounts, calculatePermissionApplications]);

  useEffect(() => {
    const handleLeaveApproved = async () => { await forceRefreshData(); };
    const handleLeaveRejected = async () => { await forceRefreshData(); };
    const handleLeaveUpdated = async () => { await forceRefreshData(); };
    window.addEventListener('leaveRequestApproved', handleLeaveApproved);
    window.addEventListener('leaveRequestRejected', handleLeaveRejected);
    window.addEventListener('leaveRequestUpdated', handleLeaveUpdated);
    return () => { window.removeEventListener('leaveRequestApproved', handleLeaveApproved); window.removeEventListener('leaveRequestRejected', handleLeaveRejected); window.removeEventListener('leaveRequestUpdated', handleLeaveUpdated); };
  }, [forceRefreshData]);

  useEffect(() => {
    let lastFocusRequestTime = 0;
    const handleVisibilityChange = async () => { if (!document.hidden && user && Date.now() - lastFocusRequestTime >= 15000) { lastFocusRequestTime = Date.now(); await forceRefreshData(); } };
    const handleWindowFocus = async () => { if (user && Date.now() - lastFocusRequestTime >= 15000) { lastFocusRequestTime = Date.now(); await forceRefreshData(); } };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => { document.removeEventListener('visibilitychange', handleVisibilityChange); window.removeEventListener('focus', handleWindowFocus); };
  }, [user, forceRefreshData]);

  const handleApplyClick = (leaveType) => {
    if (managerInfo.isLoading) { showMessage('Loading manager information. Please wait...', 'info'); return; }
    if (!managerInfo.hasAssignedManager && user?.role === 'Employee') { showMessage('You cannot apply for leave because no manager has been assigned to you.', 'error'); return; }
    if (leaveType.type === 'Permission') { const total = permissionApplicationsUsed + permissionApplicationsPending; if (total >= leaveLimits.PERMISSION) { showMessage(`You have reached the maximum limit of ${leaveLimits.PERMISSION} permission applications for this month.`, 'error'); return; } }
    setSelectedLeaveType(leaveType); setShowPopup(true); setFromDate(''); setToDate('');
    setPermissionDate(''); // ✅ reset permission date
    setReason(''); setSelectedFile(null); setFileError(''); clearMessage(); setIsHalfDay(false); setHalfDayPeriod(null);
  };

  // ✅ closePopup resets permissionDate
  const closePopup = () => {
    setShowPopup(false); setSelectedLeaveType(null); setFromDate(''); setToDate('');
    setPermissionDate(''); // ✅ reset permission date
    setReason(''); setSelectedFile(null); setFileError(''); clearMessage(); setIsHalfDay(false); setHalfDayPeriod(null);
    const fi = document.getElementById('pdf-upload'); if (fi) fi.value = '';
  };
  
const getPeriodFromTime = (timeStr) => {
  if (!timeStr) return null;
  const s = String(timeStr).toLowerCase();
  if (/\bpm\b/.test(s) || s.includes('afternoon')) return 'pm';
  if (/\bam\b/.test(s) || s.includes('morning')) return 'am';
  const match = s.match(/^(\d{1,2}):(\d{2})/); // 24h dropdown value e.g. "13:00"
  if (match) return parseInt(match[1], 10) < 12 ? 'am' : 'pm';
  return null;
};

const checkDateOverlap = (newStartDate, newEndDate, isNewRequestHalfDay = false, newHalfDayPeriod = null, fromTime = null) => {
  try {
    const newStart = newStartDate instanceof Date ? newStartDate : new Date(newStartDate);
    const newEnd = newEndDate instanceof Date ? newEndDate : new Date(newEndDate);
    if (isNaN(newStart.getTime()) || isNaN(newEnd.getTime()))
      return { hasOverlap: false, conflictingRequest: null };

    const isPermission = selectedLeaveType?.type?.toLowerCase() === 'permission';

    let targetNewPeriod = null;
    if (isPermission) targetNewPeriod = getPeriodFromTime(fromTime);
    else if (isNewRequestHalfDay) targetNewPeriod = getPeriodFromTime(newHalfDayPeriod);

    let amTaken = false, pmTaken = false, permissionExists = false, conflictRequestObj = null;

    const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
    const newStartTs = dayStart(newStart);
    const newEndTs = dayStart(newEnd);
    const isNewSingleDay = newStartTs === newEndTs;

    for (const req of leaveRequests) {
      const reqStatus = String(req.status || '').replace(/_/g, ' ').trim().toLowerCase();
      if (reqStatus === 'rejected' || reqStatus === 'cancelled' || reqStatus === 'cancel') continue;

      const reqStartDate = parseDDMMYYYY(req.startDate || req.start_date);
      if (!reqStartDate || isNaN(reqStartDate.getTime())) continue;
      const reqEndRaw = parseDDMMYYYY(req.endDate || req.end_date);
      const reqStartTs = dayStart(reqStartDate);
      const reqEndTs = reqEndRaw && !isNaN(reqEndRaw.getTime()) ? Math.max(dayStart(reqEndRaw), reqStartTs) : reqStartTs;

      // No shared day between the two ranges → no conflict with this request
      if (reqEndTs < newStartTs || reqStartTs > newEndTs) continue;

      // Either side spans several days → any shared day is a full conflict
      if (!isNewSingleDay || reqStartTs !== reqEndTs) {
        return { hasOverlap: true, conflictingRequest: req };
      }

      const reqType = String(req.leaveType || req.type || '').trim().toLowerCase();
      const durationStr = String(req.duration || '').toLowerCase();
      const reasonStr = String(req.reason || '').toLowerCase();
      const fallbackPeriodText = String(req.duration_period || req.halfDayPeriod || req.period || '').toLowerCase();

      const isReqHalf = req.daysRequested === 0.5 || req.isHalfDay === true ||
        durationStr.includes('half') || durationStr.includes('morning') || durationStr.includes('afternoon');

      let reqPeriod = null;
      if (/\bmorning\b/.test(reasonStr) || /\bam\b/.test(reasonStr) || durationStr.includes('morning') || fallbackPeriodText.includes('morn') || fallbackPeriodText === 'am') reqPeriod = 'am';
      if (/\bafternoon\b/.test(reasonStr) || /\bpm\b/.test(reasonStr) || durationStr.includes('afternoon') || fallbackPeriodText.includes('after') || fallbackPeriodText === 'pm') reqPeriod = 'pm';

      if (reqType === 'permission') {
        permissionExists = true; conflictRequestObj = req;
        if (reqPeriod === 'am') amTaken = true;
        if (reqPeriod === 'pm') pmTaken = true;
      } else if (isReqHalf) {
        conflictRequestObj = req;
        if (reqPeriod === 'pm') pmTaken = true; else amTaken = true;
      } else {
        conflictRequestObj = req;
        if (durationStr.includes('morning')) amTaken = true;
        else if (durationStr.includes('afternoon')) pmTaken = true;
        else { amTaken = true; pmTaken = true; }
      }
    }

    if (isPermission && permissionExists) return { hasOverlap: true, conflictingRequest: conflictRequestObj };
    if (amTaken && pmTaken) return { hasOverlap: true, conflictingRequest: conflictRequestObj };

    if (isPermission) {
      if (!targetNewPeriod) return { hasOverlap: true, conflictingRequest: conflictRequestObj };
      if (targetNewPeriod === 'am' && amTaken) return { hasOverlap: true, conflictingRequest: conflictRequestObj };
      if (targetNewPeriod === 'pm' && pmTaken) return { hasOverlap: true, conflictingRequest: conflictRequestObj };
      return { hasOverlap: false, conflictingRequest: null };
    }

    if (isNewRequestHalfDay) {
      if (targetNewPeriod === 'am' && amTaken) return { hasOverlap: true, conflictingRequest: conflictRequestObj };
      if (targetNewPeriod === 'pm' && pmTaken) return { hasOverlap: true, conflictingRequest: conflictRequestObj };
      return { hasOverlap: false, conflictingRequest: null };
    }

    if (amTaken || pmTaken || permissionExists) return { hasOverlap: true, conflictingRequest: conflictRequestObj };
    return { hasOverlap: false, conflictingRequest: null };
  } catch (err) {
    return { hasOverlap: false, conflictingRequest: null };
  }
};
  const validateForm = () => {
    if (!managerInfo.hasAssignedManager && user?.role === 'Employee') { showMessage('Cannot submit leave request. No manager assigned.', 'error'); return false; }
    if (!fromDate) { showMessage('Please select a start date/time.', 'error'); return false; }
    if (!toDate) { showMessage('Please select an end date/time.', 'error'); return false; }
    if (!reason.trim()) { showMessage('Please provide a reason for your leave.', 'error'); return false; }
    if (reason.trim().length < 10) { showMessage('Please provide a more detailed reason (at least 10 characters).', 'error'); return false; }
    const duration = calculateDuration(fromDate, toDate);
    if (selectedLeaveType?.type?.toLowerCase() === 'permission') {
      // ✅ Validate permissionDate
      if (!permissionDate) { showMessage('Please select a permission date.', 'error'); return false; }
      // const overlapCheck = checkDateOverlap(permissionDate, permissionDate);
      const overlapCheck = checkDateOverlap(permissionDate, permissionDate, false, null, fromDate);
      if (overlapCheck.hasOverlap) return false; // shown live via liveConflict
      if (duration < leaveLimits.PERMISSION_HOURS_PER_APPLICATION) { showMessage(`Permission duration must be at least ${leaveLimits.PERMISSION_HOURS_PER_APPLICATION} hour(s) per application.`, 'error'); return false; }
      const total = permissionApplicationsUsed + permissionApplicationsPending;
      if (total >= leaveLimits.PERMISSION) { showMessage(`You have reached the maximum limit of ${leaveLimits.PERMISSION} permission applications for this month.`, 'error'); return false; }
    } else {
      // const overlapCheck = checkDateOverlap(fromDate, toDate);
      const overlapCheck = checkDateOverlap(fromDate, toDate, isHalfDay, halfDayPeriod);
      if (overlapCheck.hasOverlap) return false; // shown live via liveConflict
      if (new Date(fromDate) > new Date(toDate)) { showMessage('End date cannot be before start date.', 'error'); return false; }
      if (selectedLeaveType?.type?.toLowerCase() === 'medical leave') {
        const maxDays = actualLeaveBalance.medical?.allocated || leaveLimits.MEDICAL;
        const usedDays = leaveSummary.medical + pendingLeaveSummary.medical;
        const remainingDays = maxDays - usedDays;
        if (duration > remainingDays) { showMessage(`Insufficient Medical Leave balance. You have ${remainingDays} day(s) remaining.`, 'error'); return false; }
      }
    }
    if (selectedLeaveType?.type?.toLowerCase() === 'medical leave' && selectedFile) { const v = validateFile(selectedFile); if (!v.isValid) { showMessage(`File error: ${v.error}`, 'error'); return false; } }
    return true;
  };

  // Live overlap message — recomputed on every render, so it appears/disappears
  // the moment the dates or Full/Morning/Afternoon choice change.
  const liveConflict = (() => {
    if (!showPopup || !selectedLeaveType) return null;
    const isPerm = selectedLeaveType?.type?.toLowerCase() === 'permission';
    if (isPerm) {
      if (!permissionDate || !fromDate) return null;
      const c = checkDateOverlap(permissionDate, permissionDate, false, null, fromDate);
      if (!c.hasOverlap) return null;
      const cr = c.conflictingRequest || {};
      return `Cannot apply for permission. You already have a ${cr.leaveType || 'leave'} request (${cr.status || 'pending'}) on this date.`;
    }
    if (!fromDate || !toDate) return null;
    const c = checkDateOverlap(fromDate, toDate, isHalfDay, halfDayPeriod);
    if (!c.hasOverlap) return null;
    const cr = c.conflictingRequest || {};
    return `Cannot apply for leave. You already have a ${cr.leaveType || 'leave'} request (${cr.status || 'pending'}) that overlaps with these dates.`;
  })();

  const calculateDuration = (startDate, endDate) => {
    if (selectedLeaveType?.type?.toLowerCase() === 'permission') {
      if (startDate && endDate) { try { const s = new Date(`1970-01-01T${startDate}`); const e = new Date(`1970-01-01T${endDate}`); if (isNaN(s.getTime()) || isNaN(e.getTime())) return 1; const diff = (e - s) / (1000 * 60 * 60); if (diff < 0) return 0; return Math.max(diff, leaveLimits.PERMISSION_HOURS_PER_APPLICATION); } catch { return 1; } }
      return 1;
    } else {
      if (startDate === endDate && isHalfDay) return 0.5;
      const s = new Date(startDate); const e = new Date(endDate);
      const dayDiff = Math.ceil((e.getTime() - s.getTime()) / (1000 * 3600 * 24)) + 1;
      return dayDiff > 0 ? dayDiff : 1;
    }
  };

  const calculateTimePlus2Hours = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date(); date.setHours(hours, minutes, 0, 0);
    const newTime = new Date(date.getTime() + 2 * 60 * 60 * 1000);
    return `${String(newTime.getHours()).padStart(2, '0')}:${String(newTime.getMinutes()).padStart(2, '0')}`;
  };

 const handleSubmit = async () => {
  if (!validateForm()) return;

  if (selectedLeaveType?.type?.toLowerCase() === 'earned leave') {
    const requestedDays = calculateDuration(fromDate, toDate);
    const currentAvailableBalance = (
      actualLeaveBalance.casual.allocated +
      actualLeaveBalance.casual.extra_leave -
      actualLeaveBalance.casual.used -
      actualLeaveBalance.casual.pending
    );

    if (currentAvailableBalance - requestedDays < 0) {
      const lopDays = Math.abs(currentAvailableBalance - requestedDays).toFixed(1);

      // ✅ Hide popup FIRST so Swal appears on top
      setShowPopup(false);

      const result = await Swal.fire({
        title: 'Loss of Pay (LOP) Alert',
        html: `
          <div style="text-align:left; font-size:14px; line-height:1.8; color:#374151;">
            <p>This <strong>Earned Leave</strong> request will result in 
              <strong style="color:#dc2626;">Loss of Pay (LOP)</strong>.
            </p>
            <div style="background:#fef2f2; border:1px solid #fca5a5; 
                        border-radius:8px; padding:14px; margin:12px 0;">
              <div style="display:flex; justify-content:space-between; margin-bottom:6px;">
                <span>Current Balance:</span>
                <strong>${currentAvailableBalance.toFixed(1)} days</strong>
              </div>
              <div style="display:flex; justify-content:space-between; margin-bottom:6px;">
                <span>Requested Days:</span>
                <strong>${requestedDays.toFixed(1)} days</strong>
              </div>
              <div style="display:flex; justify-content:space-between; 
                          border-top:1px solid #fca5a5; padding-top:8px; margin-top:4px;">
                <span style="color:#dc2626; font-weight:600;">LOP Days:</span>
                <strong style="color:#dc2626;">${lopDays} days</strong>
              </div>
            </div>
            <p style="color:#6b7280; font-size:13px;">
              ⚠️ <strong>${lopDays} day(s)</strong> will be deducted from your salary.
            </p>
            <p>Do you want to proceed?</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Proceed',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        width: '420px',
        // ✅ Make sure Swal is above everything
        customClass: { container: 'swal-on-top',  title: 'text-sm font-bold text-gray-800'  },
        didOpen: () => {
          // ✅ Force z-index higher than popup overlay (9998)
          const container = document.querySelector('.swal-on-top');
          if (container) container.style.zIndex = '99999';
        }
      });

      if (!result.isConfirmed) {
        // ✅ User cancelled — reopen the leave popup with data intact
        setShowPopup(true);
        return;
      }
      // ✅ User confirmed — keep popup hidden, proceed to submit below
    }
  }

  // ✅ All submit logic runs here (outside the LOP block)
  setIsSubmitting(true);
  clearMessage();
  // ... rest of your existing submit code unchanged
    setIsSubmitting(true); clearMessage();
    const duration = calculateDuration(fromDate, toDate);
    let summaryKey = 'casual';
    if (selectedLeaveType?.type?.toLowerCase() === 'medical leave') summaryKey = 'medical';
    else if (selectedLeaveType?.type?.toLowerCase() === 'permission') summaryKey = 'permission';
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setPendingLeaveSummary(prev => ({ ...prev, [summaryKey]: prev[summaryKey] + duration }));
    if (summaryKey === 'permission') setPermissionApplicationsPending(prev => prev + 1);
    const pendingUpdate = { tempId, requestId: null, leaveType: selectedLeaveType.type, dateApplied: new Date().toISOString(), days: duration, summaryKey, timestamp: Date.now() };
    setPendingLeaveUpdates(prev => [...prev, pendingUpdate]);
    try {
      const backendUrl = getBackendUrl();
      let startDate, endDate;
      // ✅ Use permissionDate for start and end date
      if (selectedLeaveType?.type?.toLowerCase() === 'permission') {
        startDate = permissionDate || new Date().toISOString().split('T')[0];
        endDate = startDate;
      } else {
        startDate = fromDate; endDate = toDate;
      }
      const isMedicalWithFile = selectedLeaveType?.type?.toLowerCase() === 'medical leave' && selectedFile;
      if (isMedicalWithFile) {
        const formData = new FormData();
        let blt = selectedLeaveType.type.toLowerCase(); if (blt === 'medical leave') blt = 'medical';
        formData.append('leave_type', blt); formData.append('start_date', startDate); formData.append('end_date', endDate); formData.append('reason', reason.trim()); formData.append('days_requested', duration.toString());
        if (isHalfDay) { formData.append('is_half_day', 'true'); formData.append('half_day_period', halfDayPeriod); }
        formData.append('medical_document', selectedFile, selectedFile.name);
        const response = await fetch(`${backendUrl}/api/apply-leave`, { method: 'POST', credentials: 'include', body: formData });
        if (response.ok) {
          const result = await response.json(); const actualRequestId = result.leave_request?.id || result.id;
          setPendingLeaveUpdates(prev => prev.map(p => p.tempId === tempId ? { ...p, requestId: actualRequestId } : p));
          const managerText = managerInfo.hasAssignedManager && managerInfo.managerName ? managerInfo.managerName : 'your assigned manager';
          showTopSuccessMessage(`${selectedLeaveType.type} request submitted successfully! Sent to ${managerText} for approval.`);
          window.dispatchEvent(new CustomEvent('leaveRequestUpdated', { detail: { action: 'submitted', leaveType: selectedLeaveType.type, requestId: actualRequestId, timestamp: Date.now() } }));
          setTimeout(() => forceRefreshData(), 1000); closePopup();
        } else {
          setPendingLeaveSummary(prev => ({ ...prev, [summaryKey]: Math.max(0, prev[summaryKey] - duration) }));
          if (summaryKey === 'permission') setPermissionApplicationsPending(prev => Math.max(0, prev - 1));
          setPendingLeaveUpdates(prev => prev.filter(p => p.tempId !== tempId));
          try { const ct = response.headers.get('content-type'); const ed = ct?.includes('application/json') ? await response.json() : null; showMessage(`Failed to submit: ${ed?.error || ed?.message || 'Unknown error'}`, 'error'); } catch { showMessage('Failed to submit leave request.', 'error'); }
        }
      } else {
        let blt = selectedLeaveType?.type?.toLowerCase(); if (blt === 'earned leave') blt = 'casual'; else if (blt === 'medical leave') blt = 'medical'; else if (blt === 'permission') blt = 'emergency';
       let finalReason = reason.trim();
        let payloadHalfDayPeriod = halfDayPeriod;
        if (selectedLeaveType?.type?.toLowerCase() === 'permission') {
          const f12 = new Date(`2000-01-01T${fromDate}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          const t12 = new Date(`2000-01-01T${toDate}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          finalReason += `\n\nTime: ${f12} to ${t12}`;
          const startHour = fromDate ? parseInt(fromDate.split(':')[0], 10) : null;
          payloadHalfDayPeriod = (startHour !== null && !isNaN(startHour)) ? (startHour < 12 ? 'morning' : 'afternoon') : null;
        }
        const leaveData = { leave_type: blt, start_date: startDate, end_date: endDate, reason: finalReason, days_requested: duration, is_half_day: isHalfDay, half_day_period: halfDayPeriod };
        const response = await fetch(`${backendUrl}/api/apply-leave`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(leaveData) });
        if (response.ok) {
          const result = await response.json(); const actualRequestId = result.leave_request?.id || result.id;
          setPendingLeaveUpdates(prev => prev.map(p => p.tempId === tempId ? { ...p, requestId: actualRequestId } : p));
          const managerText = managerInfo.hasAssignedManager && managerInfo.managerName ? managerInfo.managerName : 'your assigned manager';
          showTopSuccessMessage(`${selectedLeaveType.type} request submitted successfully! Sent to ${managerText} for approval.`);
          window.dispatchEvent(new CustomEvent('leaveRequestUpdated', { detail: { action: 'submitted', leaveType: selectedLeaveType.type, requestId: actualRequestId, timestamp: Date.now() } }));
          setTimeout(() => forceRefreshData(), 1000); closePopup();
        } else {
          setPendingLeaveSummary(prev => ({ ...prev, [summaryKey]: Math.max(0, prev[summaryKey] - duration) }));
          if (summaryKey === 'permission') setPermissionApplicationsPending(prev => Math.max(0, prev - 1));
          setPendingLeaveUpdates(prev => prev.filter(p => p.tempId !== tempId));
          try { const ct = response.headers.get('content-type'); const ed = ct?.includes('application/json') ? await response.json() : null; showMessage(`Failed to submit: ${ed?.error || ed?.message || 'Unknown error'}`, 'error'); } catch { showMessage('Failed to submit leave request.', 'error'); }
        }
      }
    } catch (error) {
      setPendingLeaveSummary(prev => ({ ...prev, [summaryKey]: Math.max(0, prev[summaryKey] - duration) }));
      if (summaryKey === 'permission') setPermissionApplicationsPending(prev => Math.max(0, prev - 1));
      setPendingLeaveUpdates(prev => prev.filter(p => p.tempId !== tempId));
      showMessage(`Network error: ${error.message}. Please try again.`, 'error');
    } finally { setIsSubmitting(false); }
  };

  // const filteredRequests = leaveRequests.filter(r => {
  //   if (!searchTerm) return true;
  //   const sl = searchTerm.toLowerCase();  
  //   return r.employeeName?.toLowerCase().includes(sl) || r.leaveType?.toLowerCase().includes(sl) || r.status?.toLowerCase().includes(sl) || r.reason?.toLowerCase().includes(sl);
  // });

  const LEAVE_TYPE_ALIASES = {
  'earned leave':    'casual',
  'el':              'casual',
  'casual leave':    'casual',
  'sick leave':      'medical',
  'medical leave':   'medical',
  'sl':              'medical',
  'permission':      'emergency',
  'pm':              'emergency',
};

const isBackfill = (r) => /^\s*backfill\b/i.test(String(r.reason || ''));
const visibleRequests = leaveRequests.filter(r => !isBackfill(r));

const filteredRequests = visibleRequests.filter(r => {
  if (!searchTerm) return true;
  const sl = searchTerm.toLowerCase().trim();

  // display label for this row's leave type (what user sees in table)
  const displayLeaveType = r.leaveType === 'Casual Leave' ? 'earned leave' : (r.leaveType || '').toLowerCase();

  // resolve what user typed to a DB keyword
  const resolvedTerm = LEAVE_TYPE_ALIASES[sl] || sl;

  return (
    r.employeeName?.toLowerCase().includes(sl) ||
    r.leaveType?.toLowerCase().includes(resolvedTerm) ||   // matches DB value
    displayLeaveType.includes(sl) ||                        // matches display label
    r.status?.toLowerCase().includes(sl) ||
    r.reason?.toLowerCase().includes(sl)
  );
});

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const displayedRequests = showAllRequests ? filteredRequests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : filteredRequests.slice(0, 10);
  const toggleViewAllRequests = () => { setShowAllRequests(!showAllRequests); setCurrentPage(1); };
  // ✅ isFormValid includes permissionDate check for permission type
  const isFormValid = () => {
    const baseValid = !managerInfo.isLoading && !(!managerInfo.hasAssignedManager && user?.role === 'Employee') && fromDate && toDate && reason.trim().length >= 10 && !(selectedFile && fileError);
    if (selectedLeaveType?.type?.toLowerCase() === 'permission') {
      return baseValid && !!permissionDate;
    }
    return baseValid;
  };

  /* ── leave card data ── */
  const leaveDataArray = [
    {
      color: '#101F40', bg: '#eef1ff', tag: 'EL', tagColor: '#101F40', type: 'Earned Leave',
      used: leaveSummary.casual,
      pending: pendingLeaveSummary.casual,
      total: actualLeaveBalance.casual.allocated + actualLeaveBalance.casual.extra_leave,
      // available = allocated - confirmed used only; pending does NOT reduce available
      available: Math.max(0, (actualLeaveBalance.casual.allocated + actualLeaveBalance.casual.extra_leave - leaveSummary.casual)),
      unit: 'days',
      disabled: managerInfo.isLoading || (!managerInfo.hasAssignedManager && user?.role === 'Employee'),
      lop: (() => {
        const total = actualLeaveBalance.casual.allocated + actualLeaveBalance.casual.extra_leave;
        const used = leaveSummary.casual;
        // Only show LOP after at least one approved request exists
        return used > total ? parseFloat((used - total).toFixed(1)) : 0;
      })(),
    },
    {
      color: '#4caf4f', bg: '#ecfdf5', tag: 'SL', tagColor: '#4caf4f', type: 'Medical Leave',
      used: leaveSummary.medical,
      pending: pendingLeaveSummary.medical,
      total: leaveLimits.MEDICAL,
      // available = allocated - confirmed used only
      available: Math.max(0, leaveLimits.MEDICAL - leaveSummary.medical),
      unit: 'days',
      // disabled only when confirmed used hits the limit (not pending)
      disabled: managerInfo.isLoading || (!managerInfo.hasAssignedManager && user?.role === 'Employee') || (leaveLimits.MEDICAL - leaveSummary.medical) <= 0,
      lop: Math.max(0, leaveSummary.medical - leaveLimits.MEDICAL),
    },
    {
      color: '#f59e0b', bg: '#fffbeb', tag: '', tagColor: '#f59e0b', type: 'Permission',
      used: permissionApplicationsUsed,
      pending: permissionApplicationsPending,
      total: leaveLimits.PERMISSION,
      // available = allocated - confirmed approved only
      available: Math.max(0, leaveLimits.PERMISSION - permissionApplicationsUsed),
      unit: 'hrs',
      // disabled only when confirmed approved hits the limit
      disabled: managerInfo.isLoading || (!managerInfo.hasAssignedManager && user?.role === 'Employee') || permissionApplicationsUsed >= leaveLimits.PERMISSION,
       lop: Math.max(0, permissionApplicationsUsed - leaveLimits.PERMISSION),
    },
  ];

  /* ── Card SVG icons ── */
  const iconSize = isMobile ? 18 : 22;
  const CardIconEL = () => (<svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="none" stroke="#101F40" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
  const CardIconSL = () => (<svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="none" stroke="#4caf4f" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>);
  const CardIconPM = () => (<svg viewBox="0 0 24 24" width={iconSize} height={iconSize} fill="none" stroke="#f59e0b" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
  const cardIcons = [<CardIconEL />, <CardIconSL />, <CardIconPM />];

  /* ── mobile-hidden table columns ── */
  const hiddenOnMobile = ['Employee', 'Date Range'];

  /* ═══════════════════════════════════════════
      RENDER
  ═══════════════════════════════════════════ */
  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Playfair+Display:wght@700&display=swap" rel="stylesheet" />

      {/* Top success banner */}
      {showTopMessage && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, backgroundColor: '#4CAF4F', color: '#fff', padding: isMobile ? '10px 14px' : '14px 20px', textAlign: 'center', zIndex: 99999, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', fontFamily: "'DM Sans', sans-serif", fontSize: isMobile ? 13 : 14 }}>
          <span>{topMessage}</span>
          <button onClick={() => setShowTopMessage(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', marginLeft: 12 }}></button>
        </div>
      )}

      <div style={{ ...S.shell, marginTop: showTopMessage ? (isMobile ? 44 : 52) : 0 }}>

        {/* ── Sidebar / Mobile top nav ── */}
        <div style={S.sidebar}>
          <div style={S.sidebarLogo}></div>

          {/* Person → My Dashboard (overview) */}
          <div
            style={S.sidebarIcon(activeView === 'overview')}
            title="My Dashboard"
            onClick={() => setActiveView('overview')}
          >
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none"
              stroke={activeView === 'overview' ? '#fff' : 'rgba(255,255,255,0.7)'}
              strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
          </div>

          {/* Calendar → My Attendance (in-page tab, no route change) */}
          <div
            style={S.sidebarIcon(activeView === 'attendance')}
            title="My Attendance"
            onClick={() => setActiveView('attendance')}
          >
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none"
              stroke={activeView === 'attendance' ? '#fff' : 'rgba(255,255,255,0.7)'}
              strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>             

          {!isMobile && <div style={S.sidebarSpacer} />}
        </div>

        {/* ── Main ── */}
        <div style={S.main}>

          {/* Top Bar */}
          <div style={S.topbar}>
            <div>
              <span style={S.companyName}>Manureva Digital Solutions</span>
            </div>
            <div style={S.topbarRight}>
              {isMobile && (
                <button style={S.notifBtn} onClick={() => setShowMobileSearch(v => !v)} title="Search">
                  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#555" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </button>
              )}
              {!isMobile && (
                <div style={S.searchWrap}>
                  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#999" strokeWidth={2} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input type="text" placeholder="Search leave requests…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={S.searchInput} />
                </div>
              )}
              <button style={S.notifBtn} onClick={handleNotificationClick} title={notificationCount > 0 ? `${notificationCount} new notification(s)` : 'Notifications'}>
                <svg viewBox="0 0 24 24" width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} fill="none" stroke="#555" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {notificationCount > 0 && <div style={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, background: '#ef4444', borderRadius: '50%', border: '1.5px solid #fff' }} />}
              </button>
              {/* <WorkerTickets user={user} isMobile={isMobile} /> */}
              <div style={S.avatarChip} onClick={handleProfileClick} title="Edit profile picture">
                {profilePicture && profilePicture !== DefaultProfile
                  ? <img src={profilePicture} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                  : getInitials()}
              </div>
              <button style={S.powerBtn} onClick={handleLogout} title="Logout">
                <svg viewBox="0 0 24 24" width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} fill="none" stroke="#888" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile expandable search */}
          {isMobile && showMobileSearch && (
            <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e8e4df' }}>
              <div style={{ position: 'relative' }}>
                <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#999" strokeWidth={2} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input autoFocus type="text" placeholder="Search leave requests…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ width: '100%', background: '#f5f3ef', border: 'none', borderRadius: 20, padding: '8px 16px 8px 32px', fontSize: 13, color: '#555', outline: 'none', fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box' }} />
              </div>
            </div>
          )}

          {/* Content */}
          {activeView === 'overview' && (
          <div style={S.content}>

            {/* ── Hero Card ── */}
            <div style={S.heroCard}>
              <div style={{ position: 'absolute', top: -40, right: -40, width: isMobile ? 120 : 200, height: isMobile ? 120 : 200, background: 'rgba(79,110,247,0.15)', borderRadius: '50%' }} />
              <div style={{ position: 'absolute', bottom: -60, right: 80, width: isMobile ? 80 : 150, height: isMobile ? 80 : 150, background: 'rgba(139,92,246,0.10)', borderRadius: '50%' }} />
              <div style={S.heroLeft}>
                <div style={{ minWidth: 0 }}>
                  <h2 style={S.heroInfoH2}>Welcome back, {user?.name || 'User'}!</h2>

                 <div style={{ 
                  fontSize: isMobile ? 11 : 12, 
                  color: 'rgba(255,255,255,0.5)', 
                  marginBottom: 8,
                  wordBreak: 'break-all',
                  maxWidth: isMobile ? 150 : 220,
                  // textAlign: isMobile ? 'left' : 'right',
                }}>
                  {user?.email || ''}
                </div>

                  {managerInfo.isLoading ? (
                    <div style={S.managerPill}><span style={S.managerPillText}>Loading manager info…</span></div>
                  ) : managerInfo.hasAssignedManager ? (
                    <div style={S.managerPill}>
                      <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="#8ba7ff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      <span style={S.managerPillText}>Reporting Manager:</span>
                      <strong style={S.managerPillStrong}>{managerInfo.managerName}</strong>
                    </div>
                  ) : (
                    <div style={S.noMgrPill}>
                      <FaExclamationTriangle style={{ color: '#f59e0b', fontSize: 12 }} />
                      <span style={{ fontSize: isMobile ? 11 : 12, color: '#fbbf24' }}>No Manager Assigned — Contact HR</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={S.heroRight} className="w-hero-right">
                
                <div style={S.dateLabel}>Today</div>
                <div style={S.dateVal}>{isMobile ? todayStrShort : todayStr}</div>
              </div>
            </div>

            {/* ── Leave Cards ── */}
            <div style={S.cardsRow} className="w-cards-row">
              {leaveDataArray.map((leave, idx) => {
                // usedPct = confirmed approved only; pendingPct = pending on top
                const usedPct    = leave.total > 0 ? Math.min(100, (leave.used    / leave.total) * 100) : 0;
                const pendingPct = leave.total > 0 ? Math.min(100 - usedPct, (leave.pending / leave.total) * 100) : 0;
                const hovered = cardHover === idx;
                return (
                  <div key={leave.type} className="w-card" style={{ ...S.leaveCard, ...(hovered && !isMobile ? { transform: 'translateY(-3px)', boxShadow: '0 10px 28px rgba(0,0,0,0.09)' } : {}) }} onMouseEnter={() => setCardHover(idx)} onMouseLeave={() => setCardHover(null)}>
                    <div style={S.cardAccent(leave.color)} />
                    <div style={S.cardTop}>
                      <div style={S.cardIcon(leave.bg)}>{cardIcons[idx]}</div>
                      <span style={S.cardTag(leave.bg, leave.color)}>{leave.tag}</span>
                    </div>
                    <div style={S.cardTitle}>{leave.type}</div>
                    <div style={S.cardCount}>
                      {isLoadingSummary ? '…' : leave.used}
                      <span style={S.cardCountSub}> / {leave.total} {leave.unit}</span>
                      {!isLoadingSummary && leave.pending > 0 && (
                        <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 500, color: '#f59e0b', marginLeft: 8,
                          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 20,
                          padding: '2px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          +{leave.pending} pending
                        </span>
                      )}
                    </div>
                    <div style={S.cardMeta}>
                      <div style={S.cardBarWrap}>
                        <div style={{ height:'100%', display:'flex', borderRadius:3, overflow:'hidden' }}>
                          <div style={{ width:`${usedPct}%`, background: leave.color, transition:'width 0.8s ease' }} />
                          <div style={{ width:`${pendingPct}%`, background: '#fbbf24', opacity:0.6, transition:'width 0.8s ease' }} />
                        </div>
                      </div>
                      <div style={S.cardFooter}>
                        <div style={S.cardStat}>
                           <strong  style={{ color: '#000' }}>Used: </strong><strong style={{ color: '#555', fontWeight: 500 }}>{leave.used}</strong>
                          &nbsp;·&nbsp; <strong style={{ color: '#000' }}>Avail: </strong><strong style={{ color: '#15803d', fontWeight: 600 }}>{leave.available}</strong>
                          {leave.type === 'Earned Leave' && leave.lop > 0 && (
                            <>
                              &nbsp;·&nbsp;<strong style={{ color: '#000' }}>LOP: </strong><strong style={{ color: leave.lop > 0 ? '#dc2626' : '#15803d', fontWeight: 600 }}>
                                {leave.lop ?? 0}
                              </strong>
                            </>
                          )}
                        </div>
                        <button style={S.applyBtn(leave.color, leave.disabled || isLoadingSummary)} onClick={() => handleApplyClick(leave)} disabled={leave.disabled || isLoadingSummary}>
                          {isLoadingSummary ? '…' : leave.disabled ? (managerInfo.isLoading ? '…' : !managerInfo.hasAssignedManager ? 'No Mgr' : 'Limit') : 'Apply'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {summaryError && (
              <div style={{ padding: 12, background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: "'DM Sans', sans-serif", fontSize: isMobile ? 12 : 14 }}>
                <span>{summaryError}</span>
                <button onClick={() => window.location.reload()} style={{ padding: '4px 8px', background: '#ffc107', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Retry</button>
              </div>
            )}

            {/* ── Recent Requests Table ── */}
            <div style={S.tableSection} ref={recentRequestRef} className="w-table-section">
              <div style={S.tableHead} className="w-table-head">
                <div style={S.tableTitle} className="w-table-title">Recent Leave Requests</div>
                <button style={S.policyBtn} onClick={() => setShowLeavePolicyModal(true)} className="w-policy-btn">📋 {isMobile ? 'Policy' : 'Leave Policy'}</button>
              </div>
              {requestsError && (
                <div style={{ padding: '12px 16px', background: '#fff3cd', borderBottom: '1px solid #ffc107', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>{requestsError}</span>
                  <button onClick={() => window.location.reload()} style={{ padding: '4px 8px', background: '#ffc107', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Retry</button>
                </div>
              )}
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 480 : 'auto' }}>
                  <thead>
                    <tr>
                      {['Date Applied', 'Employee', 'Leave Type', 'Duration', 'Date Range', 'Status', 'Comments'].map(h => {
                        const isHidden = isMobile && hiddenOnMobile.includes(h);
                        let colClass = "";
                        if (h === 'Employee') colClass = "w-col-employee";
                        if (h === 'Date Range') colClass = "w-col-daterange";
                        if (isHidden) return null;
                        return <th key={h} style={S.th} className={`w-th ${colClass}`}>{h}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingRequests ? (
                      //req.status is approved make the row editable and add option for cancellation popup.
                      <tr><td colSpan={isMobile ? 5 : 7} style={{ ...S.td, textAlign: 'center', color: '#aaa', padding: 32 }}>Loading leave requests…</td></tr>
                    ) : displayedRequests.length > 0 ? displayedRequests.map((req, idx) => (
                      console.log('Rendering request:', req),
                      <tr key={req.id || idx} style={{ borderTop: '1px solid #f5f3ef' }}>
                        <td style={{ ...S.td, color: '#888', whiteSpace: 'nowrap' }} className="w-td">{req.dateApplied}</td>
                        {!isMobile && <td style={{ ...S.td, fontWeight: 500, color: '#101F40' }} className="w-td w-col-employee">{req.employeeName}</td>}
                        <td style={S.td} className="w-td"><span style={S.leaveChip(req.leaveType)}>{req.leaveType === 'Casual Leave' ? 'Earned Leave' : req.leaveType}</span></td>
                        {/* <td style={{ ...S.td, whiteSpace: 'nowrap' }} className="w-td">{req.duration}{req.duration_period ? ` (${req.duration_period.toUpperCase()})` : ''}</td> */}
                        {(() => {
                          const leaveTypeLower = String(req.leaveType || '').toLowerCase();
                          const reasonStr = String(req.reason || '');

                          // 1. IF IT IS A PERMISSION
                          if (leaveTypeLower === 'permission') {
                            const timeMatch = reasonStr.match(/Time:\s*(\d+:\d+\s*(?:AM|PM)\s*to\s*\d+:\d+\s*(?:AM|PM))/i);
                            const extractedTime = timeMatch ? timeMatch[1] : '';

                            return (
                              <td style={{ ...S.td, whiteSpace: 'nowrap' }} className="w-td">
                                {req.duration}
                                {extractedTime && <span style={S.hintBadge}>{extractedTime}</span>}
                              </td>
                            );
                          }

                          // 2. IF IT IS A REGULAR LEAVE (Half Day / Full Day)
                          let periodIndicator = req.duration_period || '';
                          if (!periodIndicator) {
                            if (reasonStr.toLowerCase().includes('morning')) periodIndicator = 'Morning';
                            if (reasonStr.toLowerCase().includes('afternoon')) periodIndicator = 'Afternoon';
                          }

                          return (
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }} className="w-td">
                              {req.duration}
                              {periodIndicator && <span style={S.hintBadge}>{periodIndicator.trim().toUpperCase()}</span>}
                            </td>
                          );
                        })()}

                        {!isMobile && <td style={{ ...S.td, color: '#888', whiteSpace: 'nowrap' }} className="w-td w-col-daterange">{req.dateRange}</td>}
                        <td style={S.td} className="w-td">
                          <span style={S.statusBadge(req.status)}>{normalizeStatusText(req.status)}</span>
                        </td>
                        <td
                          style={{
                            ...S.td,
                            maxWidth: isMobile ? 100 : 200,
                          }}
                          className="w-td"
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{
                              color: '#aaa', fontStyle: 'italic',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              flex: 1, minWidth: 0,
                            }}>
                              {req.managerComments && (req.status === 'Rejected' || req.status === 'Approved')
                                ? req.managerComments
                                : req.status === 'Pending'
                                  ? 'Under review'
                                  : req.reason}
                            </span>

                            {req.status === 'Approved' && (
                              <button
                                onClick={() => { setCancelTarget(req); setShowCancelModal(true); }}
                                style={{
                                  padding: '3px 10px',
                                  fontSize: isMobile ? 10 : 11,
                                  fontWeight: 600,
                                  border: 'none',
                                  borderRadius: 20,
                                  background: '#fee2e2',
                                  color: '#b91c1c',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                  fontFamily: "'DM Sans', sans-serif",
                                }}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={isMobile ? 5 : 7} style={{ ...S.td, textAlign: 'center', color: '#bbb', padding: 40 }}>{searchTerm ? 'No requests match your search' : 'No leave requests found'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filteredRequests.length > 10 && (
                <div style={S.paginationWrap}>
                  <button onClick={toggleViewAllRequests} style={{ padding: isMobile ? '8px 16px' : '9px 22px', background: showAllRequests ? '#6b7280' : '#101F40', color: '#fff', border: 'none', borderRadius: 20, fontSize: isMobile ? 12 : 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" }}>
                    {showAllRequests ? <><FaChevronUp /> Show Less</> : <><FaChevronDown /> View All ({filteredRequests.length})</>}
                  </button>
                  {showAllRequests && totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} style={{ padding: '6px 14px', background: currentPage === 1 ? '#e5e7eb' : '#101F40', color: currentPage === 1 ? '#9ca3af' : '#fff', border: 'none', borderRadius: 6, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>Prev</button>
                      <span style={{ fontSize: 13, color: '#374151', fontWeight: 500, minWidth: 80, textAlign: 'center' }}>Page {currentPage}/{totalPages}</span>
                      <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} style={{ padding: '6px 14px', background: currentPage === totalPages ? '#e5e7eb' : '#101F40', color: currentPage === totalPages ? '#9ca3af' : '#fff', border: 'none', borderRadius: 6, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>Next</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Attendance tab — in-page, no route change */}
          {activeView === 'attendance' && (
            <AttendancePanel user={user} isMobile={isMobile} />
          )}
        </div>
      </div>

      {/* ── Apply Leave Popup ── */}
      {showPopup && (() => {
        const typeName     = selectedLeaveType?.type || 'Leave';
        const isPermission = typeName.toLowerCase() === 'permission';
        const isMedical    = typeName.toLowerCase().includes('medical');
        const accent       = selectedLeaveType?.color || '#4f6ef7';
        const accentBg     = selectedLeaveType?.bg || '#eef1ff';
        const isSingleDay  = !isPermission && fromDate && toDate && fromDate === toDate;

        const fmtDay = (iso) => iso
          ? new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        const fmt12 = (hhmm) => hhmm
          ? new Date(`2000-01-01T${hhmm}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
          : '';

        // ── Balance shown in the header ──
        let balanceMain = '', balanceDetail = '';
        if (typeName === 'Earned Leave') {
          const c = actualLeaveBalance.casual;
          const avail = c.allocated + c.extra_leave - c.used - c.pending;
          balanceMain   = `${avail.toFixed(1)} days left`;
          balanceDetail = `${c.used.toFixed(1)} used${c.pending > 0 ? `, ${c.pending.toFixed(1)} pending` : ''}`;
        } else if (typeName === 'Medical Leave') {
          const m = actualLeaveBalance.medical;
          balanceMain   = `${m.available.toFixed(1)} of ${m.allocated} days left`;
          balanceDetail = `${m.used.toFixed(1)} used${pendingLeaveSummary.medical > 0 ? `, ${pendingLeaveSummary.medical.toFixed(1)} pending` : ''}`;
        } else if (isPermission) {
          const p = actualLeaveBalance.permission;
          balanceMain   = `${p.available} of ${p.allocated} left this month`;
          balanceDetail = `${permissionApplicationsUsed} used${permissionApplicationsPending > 0 ? `, ${permissionApplicationsPending} pending` : ''}`;
        }

        // ── Summary of what is being requested ──
        let summary = '', summaryWarn = false, dayCount = 0;
        if (fromDate && toDate) {
          if (isPermission) {
            const dur = calculateDuration(fromDate, toDate);
            const min = leaveLimits.PERMISSION_HOURS_PER_APPLICATION;
            if (dur <= 0)        { summary = 'End time is before the start time'; summaryWarn = true; }
            else if (dur < min)  { summary = `${dur}h is below the ${min}h minimum`; summaryWarn = true; }
            else                   summary = `${dur} hour${dur !== 1 ? 's' : ''}, ${fmt12(fromDate)} to ${fmt12(toDate)}`;
          } else {
            dayCount = calculateDuration(fromDate, toDate);
            summary = isHalfDay
              ? `Half day, ${halfDayPeriod === 'afternoon' ? 'afternoon' : 'morning'}`
              : `${dayCount} day${dayCount !== 1 ? 's' : ''}`;
          }
        }

        const dayChoice = !isHalfDay ? 'full' : (halfDayPeriod === 'afternoon' ? 'afternoon' : 'morning');
        const DAY_OPTIONS = [
          { key: 'full',      label: 'Full day',  sub: '1 day' },
          { key: 'morning',   label: 'Morning',   sub: 'Half day' },
          { key: 'afternoon', label: 'Afternoon', sub: 'Half day' },
        ];
        const pickDay = (key) => {
          if (key === 'full') { setIsHalfDay(false); setHalfDayPeriod(null); }
          else { setIsHalfDay(true); setHalfDayPeriod(key); }
        };

        const canSubmit = !isSubmitting && isFormValid() && !liveConflict;
        const submitLabel = isSubmitting
          ? 'Sending…'
          : (!isPermission && dayCount > 0 ? `Send request (${dayCount} day${dayCount !== 1 ? 's' : ''})` : 'Send request');

        const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
        const fieldStyle = (active) => ({
          width: '100%', minHeight: 44, padding: '10px 12px', borderRadius: 10,
          border: `1px solid ${active ? accent : '#e2ddd6'}`, background: '#fff',
          fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: '#111827',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          cursor: 'pointer', boxSizing: 'border-box', textAlign: 'left',
        });
        const calIcon = (
          <svg aria-hidden="true" viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#9ca3af" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        );

        return (
          <div className="lp-overlay"
            style={{ position: 'fixed', inset: 0, background: 'rgba(16,31,64,0.55)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 9998, padding: isMobile ? 0 : 20, fontFamily: "'DM Sans', sans-serif" }}>
            <style>{`
              .lp-modal button:focus-visible, .lp-modal select:focus-visible, .lp-modal textarea:focus-visible {
                outline: 2px solid ${accent}; outline-offset: 2px;
              }
              .lp-modal textarea:focus { border-color: ${accent}; }
              @keyframes lpSheet { from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; } }
              .lp-modal { animation: lpSheet 0.22s ease-out; }
              @media (prefers-reduced-motion: reduce) { .lp-modal { animation: none; } }
            `}</style>

            <div className="lp-modal" role="dialog" aria-modal="true" aria-labelledby="lp-title" onClick={e => e.stopPropagation()}
              style={{ background: '#fff', width: '100%', maxWidth: isMobile ? '100%' : 520, maxHeight: isMobile ? '94vh' : '90vh', borderRadius: isMobile ? '20px 20px 0 0' : 18, boxShadow: '0 24px 64px rgba(16,31,64,0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* ── Header: type, balance, approver ── */}
              <div style={{ padding: isMobile ? '10px 18px 16px' : '22px 24px 18px', borderBottom: '1px solid #f0ede8', borderTop: `4px solid ${accent}` }}>
                {isMobile && <div aria-hidden="true" style={{ width: 40, height: 4, borderRadius: 2, background: '#e2ddd6', margin: '0 auto 12px' }} />}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <h2 id="lp-title" style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 600, color: '#101F40', letterSpacing: '-0.3px' }}>
                      Apply for {typeName.toLowerCase()}
                    </h2>
                    {balanceMain && (
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 10px' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: accent === '#101F40' ? '#4f6ef7' : accent, background: accentBg, padding: '3px 10px', borderRadius: 999 }}>
                          {balanceMain}
                        </span>
                        <span style={{ fontSize: 12.5, color: '#6b7280' }}>{balanceDetail}</span>
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={closePopup} aria-label="Close"
                    style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 10, border: 'none', background: '#f5f3ef', color: '#6b7280', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
                </div>

                {managerInfo.hasAssignedManager && (
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5563', minWidth: 0 }}>
                    <FaUserTie aria-hidden="true" style={{ color: '#15803d', fontSize: 13, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={managerInfo.managerEmail || ''}>
                      Goes to <strong style={{ color: '#101F40', fontWeight: 600 }}>{managerInfo.managerName}</strong> for approval
                      {!isMobile && managerInfo.managerEmail && <span style={{ color: '#9ca3af' }}> ({managerInfo.managerEmail})</span>}
                    </span>
                  </div>
                )}
              </div>

              {/* ── Scrollable body ── */}
              <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '18px' : '22px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

                {(liveConflict || message) && (() => {
                  const bannerText = liveConflict || message;
                  const bannerType = liveConflict ? 'error' : messageType;
                  return (
                  <div role="alert" style={{ padding: '10px 14px', borderRadius: 10, fontSize: 13, border: '1px solid', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
                    ...(bannerType === 'error' ? { background: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b' }
                      : bannerType === 'success' ? { background: '#f0fdf4', borderColor: '#86efac', color: '#065f46' }
                      : { background: '#eff6ff', borderColor: '#93c5fd', color: '#1e40af' }) }}>
                    <span>{bannerText}</span>
                    {!liveConflict && (
                      <button type="button" onClick={clearMessage} aria-label="Dismiss message" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'inherit', padding: '0 4px', flexShrink: 0 }}>×</button>
                    )}
                  </div>
                  );
                })()}

                {/* ── When ── */}
                {isPermission ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <span style={labelStyle}>Date</span>
                      <div style={{ position: 'relative' }}>
                        <button type="button" onClick={() => setShowPermissionCal(v => !v)} style={fieldStyle(showPermissionCal || !!permissionDate)} aria-haspopup="dialog" aria-expanded={showPermissionCal}>
                          <span style={{ color: permissionDate ? '#111827' : '#9ca3af' }}>{permissionDate ? fmtDay(permissionDate) : 'Choose a date'}</span>
                          {calIcon}
                        </button>
                        {showPermissionCal && _wkRenderCal(permissionCalMonth, setPermissionCalMonth, permissionDate, v => setPermissionDate(v), () => setShowPermissionCal(false))}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label htmlFor="lp-from-time" style={labelStyle}>Start time</label>
                        <select id="lp-from-time" value={fromDate}
                          onChange={e => { setFromDate(e.target.value); const calc = calculateTimePlus2Hours(e.target.value); setToDate(calc || ''); }}
                          style={{ ...fieldStyle(!!fromDate), appearance: 'auto' }}>
                          <option value="">Select</option>
                          {Array.from({ length: (19 * 60 - (9 * 60 + 30)) / 30 + 1 }, (_, i) => {
                            const mins = 9 * 60 + 30 + i * 30;               // 9:30 → 19:00
                              const val = `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
                              return <option key={val} value={val}>{fmt12(val)}</option>;
                            })}
                        </select>
                      </div>
                      <div>
                        <span style={labelStyle}>End time</span>
                        <div style={{ ...fieldStyle(false), cursor: 'default', background: '#f9f8f6', color: toDate ? '#374151' : '#9ca3af' }}>
                          {toDate ? fmt12(toDate) : 'Set automatically'}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 14 : 12 }}>
                    <div>
                      <span style={labelStyle}>From</span>
                      <div style={{ position: 'relative' }}>
                        <button type="button" onClick={() => { setShowFromCal(v => !v); setShowToCal(false); }} style={fieldStyle(showFromCal || !!fromDate)} aria-haspopup="dialog" aria-expanded={showFromCal}>
                          <span style={{ color: fromDate ? '#111827' : '#9ca3af' }}>{fromDate ? fmtDay(fromDate) : 'Start date'}</span>
                          {calIcon}
                        </button>
                        {showFromCal && _wkRenderCal(fromCalMonth, setFromCalMonth, fromDate,
                          v => { setFromDate(v); if (!toDate || v > toDate) { setToDate(v); setToCalMonth(new Date(v + 'T00:00:00')); } },
                          () => setShowFromCal(false))}
                      </div>
                    </div>
                    <div>
                      <span style={labelStyle}>To</span>
                      <div style={{ position: 'relative' }}>
                        <button type="button" onClick={() => { setShowToCal(v => !v); setShowFromCal(false); }} style={fieldStyle(showToCal || !!toDate)} aria-haspopup="dialog" aria-expanded={showToCal}>
                          <span style={{ color: toDate ? '#111827' : '#9ca3af' }}>{toDate ? fmtDay(toDate) : 'End date'}</span>
                          {calIcon}
                        </button>
                        {showToCal && (
                          // Right-aligned on desktop so the calendar doesn't overflow the modal
                          <div style={{ position: 'absolute', top: '100%', [isMobile ? 'left' : 'right']: 0, width: 272 }}>
                            {_wkRenderCal(toCalMonth, setToCalMonth, toDate,
                              v => { if (fromDate && v < fromDate) return; setToDate(v); },
                              () => setShowToCal(false))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Full / half day — single-day requests only ── */}
                {isSingleDay && (
                  <div>
                    <span style={labelStyle} id="lp-day-label">How long</span>
                    <div role="radiogroup" aria-labelledby="lp-day-label"
                      style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, padding: 4, background: '#f5f3ef', borderRadius: 12 }}>
                      {DAY_OPTIONS.map(opt => {
                        const on = dayChoice === opt.key;
                        return (
                          <button key={opt.key} type="button" role="radio" aria-checked={on} onClick={() => pickDay(opt.key)}
                            style={{ padding: isMobile ? '9px 4px' : '10px 8px', border: 'none', borderRadius: 9, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                              background: on ? '#fff' : 'transparent', color: on ? '#101F40' : '#6b7280',
                              boxShadow: on ? '0 1px 3px rgba(16,31,64,0.15)' : 'none', transition: 'background 0.15s, color 0.15s' }}>
                            <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600 }}>{opt.label}</div>
                            <div style={{ fontSize: 11.5, marginTop: 2, color: on ? accent === '#101F40' ? '#4f6ef7' : accent : '#9ca3af' }}>{opt.sub}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Summary ── */}
                {summary && (
                  <div role="status" style={{ fontSize: 13.5, fontWeight: 500, padding: '10px 14px', borderRadius: 10,
                    background: summaryWarn ? '#fef2f2' : '#f7f6f3', color: summaryWarn ? '#b91c1c' : '#374151',
                    borderLeft: `3px solid ${summaryWarn ? '#ef4444' : accent}` }}>
                    {summaryWarn ? summary : <>You're requesting <strong style={{ color: '#101F40' }}>{summary}</strong>{!isPermission && fromDate !== toDate ? ` (${fmtDay(fromDate)} to ${fmtDay(toDate)})` : ''}</>}
                  </div>
                )}

                {/* ── Reason ── */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <label htmlFor="lp-reason" style={labelStyle}>Reason</label>
                    <span style={{ fontSize: 12, color: reason.length > 450 ? '#dc2626' : '#9ca3af' }}>{reason.length}/500</span>
                  </div>
                  <textarea id="lp-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
                    placeholder="A short note for your manager, e.g. family function out of town"
                    style={{ width: '100%', minHeight: isMobile ? 84 : 96, padding: '10px 12px', border: '1px solid #e2ddd6', borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box', color: '#111827' }} />
                  {reason.trim().length > 0 && reason.trim().length < 10 && (
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                      Add {10 - reason.trim().length} more character{10 - reason.trim().length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* ── Medical certificate ── */}
                {isMedical && (
                  <div>
                    <span style={labelStyle}>Medical certificate <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></span>
                    {!selectedFile ? (
                      <button type="button" onClick={() => document.getElementById('pdf-upload')?.click()}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1.5px dashed #d6d1c9', borderRadius: 10, background: '#faf9f7', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", textAlign: 'left' }}>
                        <span aria-hidden="true" style={{ fontSize: 22 }}>📄</span>
                        <span>
                          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: '#374151' }}>Attach a PDF</span>
                          <span style={{ display: 'block', fontSize: 12, color: '#9ca3af' }}>Up to 5 MB</span>
                        </span>
                      </button>
                    ) : (
                      <div style={{ padding: '10px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <span aria-hidden="true" style={{ fontSize: 20 }}>📄</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 500, color: '#065f46', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFile.name}</div>
                            <div style={{ fontSize: 11.5, color: '#6b7280' }}>{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</div>
                          </div>
                        </div>
                        <button type="button" onClick={removeFile} style={{ background: 'none', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>Remove</button>
                      </div>
                    )}
                    <input id="pdf-upload" type="file" accept=".pdf,application/pdf" onChange={handleFileSelect} style={{ display: 'none' }} />
                    {fileError && <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{fileError}</div>}
                  </div>
                )}
              </div>

              {/* ── Footer (always visible) ── */}
              <div style={{ padding: isMobile ? '12px 18px calc(12px + env(safe-area-inset-bottom, 0px))' : '16px 24px', borderTop: '1px solid #f0ede8', display: 'flex', gap: 10, background: '#fff' }}>
                <button type="button" onClick={closePopup} disabled={isSubmitting}
                  style={{ padding: '12px 18px', background: '#fff', color: '#4b5563', border: '1px solid #e2ddd6', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: isSubmitting ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                  Cancel
                </button>
                <button type="button" onClick={handleSubmit} disabled={!canSubmit}
                  style={{ flex: 1, padding: '12px', background: canSubmit ? '#101F40' : '#d1d5db', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: canSubmit ? 'pointer' : 'not-allowed', fontFamily: "'DM Sans', sans-serif", transition: 'background 0.2s' }}>
                  {submitLabel}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Leave Policy Modal */}
      {showLeavePolicyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.6)', display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', zIndex: 10000, padding: isMobile ? 0 : 20, fontFamily: "'DM Sans', sans-serif" }}>
          <div style={{ background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : 20, maxWidth: 760, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', position: 'relative' }}>
            <div style={{ padding: isMobile ? '14px 16px' : '22px 28px', borderBottom: '1px solid #f0ede8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 20, fontWeight: 700, color: '#101F40' }}>📋 Leave Policy</h2>
              <button onClick={() => setShowLeavePolicyModal(false)} style={{ background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: isMobile ? '16px' : '28px', color: '#374151', lineHeight: 1.8 }}>
              <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 12, padding: '16px 18px', marginBottom: 24 }}>
                <h3 style={{ margin: '0 0 8px', fontSize: isMobile ? 15 : 17, fontWeight: 700, color: '#065f46' }}>Revised Leave Policy – Effective 01 January 2026</h3>
                <p style={{ margin: 0, fontSize: isMobile ? 13 : 14, color: '#047857' }}>Manureva Digital Solutions India Pvt Ltd declares that the leave policy has been revised and will be applicable from 01 January 2026.</p>
              </div>
              {[
                { num: 1, color: '#4f6ef7', title: 'Earned Leave (EL)', points: ['Employees are eligible for 1 Earned Leave per month.', 'Total EL earned in a year: 12 days.', 'Carry Forward: max 5 EL days from the previous year. Any balance below 5 days will lapse.'] },
                { num: 2, color: '#ef4444', title: 'Sick Leave (SL)', points: ['Employees are eligible for 12 Sick Leave days per year.', 'Sick Leave is meant only for health-related reasons.', 'Medical proof may be required if leave exceeds company guidelines.'] },
                { num: 3, color: '#f59e0b', title: 'Sandwich Leave Policy', points: ['The Sandwich Leave Policy is applicable for the current year.', 'If leave is taken before and after a weekly off / holiday, the intervening off will be counted as leave.', 'Example: Leave on Friday and Monday → Saturday & Sunday also counted as leave.'] },
                { num: 4, color: '#8b5cf6', title: 'General Conditions', points: ['Leave must be applied and approved in advance (except emergency sick leave).', 'Leave balance will be tracked as per company records.', 'Management reserves the right to review or modify the policy.'] },
              ].map(section => (
                <div key={section.num} style={{ marginBottom: 22 }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#101F40', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ background: section.color, color: '#fff', borderRadius: '50%', width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{section.num}</span>
                    {section.title}
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: 36 }}>
                    {section.points.map((p, i) => <li key={i} style={{ marginBottom: 6, fontSize: isMobile ? 13 : 14 }}>{p}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div style={{ padding: isMobile ? '12px 16px' : '16px 28px', borderTop: '1px solid #f0ede8', display: 'flex', justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: '#fff' }}>
              <button onClick={() => setShowLeavePolicyModal(false)} style={{ padding: '11px 28px', background: '#4f6ef7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Picture Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => { if (!filePickerOpenRef.current) closeProfileModal(); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease-out' }}>
          <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%', borderRadius: '16px', padding: '0', overflow: 'hidden', animation: 'modalSlideIn 0.3s ease-out', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)' }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.95)', padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaCamera style={{ color: '#667eea' }} /> Profile Picture
              </h3>
              <button onClick={closeProfileModal} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280', padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.color = '#1f2937'} onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}>
                <FaTimes />
              </button>
            </div>
            <div style={{ background: 'white', padding: '32px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
                {previewImage ? (
                  <div style={{ textAlign: 'center' }}>
                    <img src={previewImage} alt="Preview" style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #667eea', boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)' }} />
                    <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>Preview</p>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <img src={profilePicture || '/Images/Manurevalogo.png'} alt="Current Profile" style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #667eea', boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)' }} onError={(e) => { e.target.src = '/Images/Manurevalogo.png'; }} />
                    <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>{profilePicture ? 'Current Picture' : 'Default Picture'}</p>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {previewImage ? (
                  <>
                    <button onClick={handleUploadProfilePicture} disabled={isUploadingProfile} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '16px 24px', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: isUploadingProfile ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', opacity: isUploadingProfile ? 0.7 : 1, boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)' }}>
                      {isUploadingProfile ? 'Uploading...' : <><FaUpload /> Save Profile Picture</>}
                    </button>
                    <button onClick={() => setPreviewImage(null)} disabled={isUploadingProfile} style={{ background: 'white', color: '#6b7280', border: '2px solid #e5e7eb', padding: '16px 24px', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: isUploadingProfile ? 'not-allowed' : 'pointer' }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { filePickerOpenRef.current = true; profileFileInputRef.current?.click(); }} disabled={isUploadingProfile} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '16px 24px', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: isUploadingProfile ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', opacity: isUploadingProfile ? 0.7 : 1, boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)' }}>
                      <FaUpload /> Upload from Device
                    </button>
                    {profilePicture && profilePicture !== DefaultProfile && (
                      <button onClick={handleRemoveProfilePicture} disabled={isUploadingProfile} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white', border: 'none', padding: '16px 24px', borderRadius: '12px', fontSize: '16px', fontWeight: '600', cursor: isUploadingProfile ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', opacity: isUploadingProfile ? 0.7 : 1, boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)' }}>
                        {isUploadingProfile ? 'Removing...' : <><FaTimes /> Remove DP</>}
                      </button>
                    )}
                  </>
                )}
              </div>
              <input ref={profileFileInputRef} type="file" accept="image/*" onChange={handleProfileFileChange} style={{ display: 'none' }} />
              <div style={{ marginTop: '20px', padding: '12px', background: '#f3f4f6', borderRadius: '8px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                <p style={{ margin: 0 }}>📸 Supported formats: JPG, PNG, GIF<br />Maximum file size: 5MB</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Panel */}
      {showNotificationPanel && (
        <div ref={notificationPanelRef} style={{ position: 'fixed', top: isMobile ? 52 : 68, right: isMobile ? 8 : 20, width: isMobile ? 'calc(100vw - 16px)' : 380, maxHeight: 480, background: '#fff', borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', zIndex: 99999, overflow: 'hidden', border: '1px solid #e8e4df', fontFamily: "'DM Sans', sans-serif" }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#101F40' }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>🔔 Notifications</span>
            <button onClick={() => setShowNotificationPanel(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 400 }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No notifications</div>
            ) : notifications.map((n, idx) => (
              <div key={n.id || idx} style={{ padding: '14px 18px', borderBottom: '1px solid #f5f3ef', background: n.isRead ? '#fff' : '#f0f7ff', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: n.type === 'approved' ? '#dcfce7' : '#fee2e2', color: n.type === 'approved' ? '#15803d' : '#b91c1c' }}>{n.status}</span>
                  <span style={{ fontSize: 11, color: '#aaa' }}>{n.date}</span>
                </div>
                <div style={{ fontSize: 13, color: '#101F40', fontWeight: 500 }}>{n.message}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                  {n.leave_type && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 500, background: '#ede9fe', color: '#6d28d9' }}>{n.leave_type}</span>}
                  {n.duration && <span style={{ fontSize: 11, color: '#6b7280' }}>📅 {n.duration}</span>}
                  {n.date_range && <span style={{ fontSize: 11, color: '#6b7280' }}>🗓 {n.date_range}</span>}
                </div>
                {n.manager_comments && n.manager_comments !== 'No comments' && (
                  <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>💬 {n.manager_comments}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {showCancelModal && cancelTarget && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(26,26,46,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999, backdropFilter: 'blur(4px)',
          padding: 20, fontFamily: "'DM Sans', sans-serif",
        }}>
          <div style={{
            background: '#fff', borderRadius: 20,
            width: '100%', maxWidth: 420,
            boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
            overflow: 'hidden',
          }}>

            {/* Header */}
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid #f0ede8',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#101F40' }}>
                Cancel Leave Request
              </h2>
              <button
                onClick={() => { setShowCancelModal(false); setCancelTarget(null); }}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#aaa', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>

              {/* Warning */}
              <div style={{
                padding: '12px 16px', borderRadius: 10, marginBottom: 20,
                background: '#fef2f2', border: '1px solid #fca5a5',
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
                <span style={{ fontSize: 13, color: '#991b1b', lineHeight: 1.6 }}>
                  Your cancellation request will be sent to the admin for approval.
                  Your balance will only be restored after the admin approves the cancellation.
                </span>
              </div>

              {/* Request details */}
              <div style={{
                padding: '14px 16px', borderRadius: 10,
                background: '#f8faff', border: '1px solid #dbe4ff',
                marginBottom: 24, fontSize: 13,
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px',
              }}>
                <div>
                  <span style={{ color: '#6b7280' }}>Type: </span>
                  <strong style={{ color: '#101F40' }}>
                    {cancelTarget.leaveType === 'Casual Leave' ? 'Earned Leave' : cancelTarget.leaveType}
                  </strong>
                </div>
                <div>
                  <span style={{ color: '#6b7280' }}>Duration: </span>
                  <strong style={{ color: '#101F40' }}>{cancelTarget.duration}</strong>
                </div>
                <div>
                  <span style={{ color: '#6b7280' }}>Applied: </span>
                  <strong style={{ color: '#101F40' }}>{cancelTarget.dateApplied}</strong>
                </div>
                <div>
                  <span style={{ color: '#6b7280' }}>Dates: </span>
                  <strong style={{ color: '#101F40' }}>{cancelTarget.dateRange}</strong>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={async () => {
                    try {
                      const backendUrl = getBackendUrl();
                      const res = await fetch(
                        `${backendUrl}/api/worker/leave-requests/${cancelTarget.id}/cancel`,
                        {
                          method: 'DELETE',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                        }
                      );
                      if (res.ok) {
                        setShowCancelModal(false);
                        setCancelTarget(null);
                        showTopSuccessMessage('Cancellation request submitted. Awaiting admin approval.');
                        setTimeout(() => forceRefreshData(), 800);
                      } else {
                        const err = await res.json().catch(() => ({}));
                        showTopSuccessMessage(`Failed to cancel: ${err?.error || err?.message || 'Server error'}`);
                        setShowCancelModal(false);
                        setCancelTarget(null);
                      }
                    } catch (e) {
                      showTopSuccessMessage(`Network error: ${e.message}`);
                      setShowCancelModal(false);
                      setCancelTarget(null);
                    }
                  }}
                  style={{
                    flex: 1, padding: '12px',
                    background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                    color: '#fff', border: 'none', borderRadius: 10,
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                    boxShadow: '0 4px 12px rgba(239,68,68,0.35)',
                  }}
                >
                  Yes, Cancel Leave
                </button>
                <button
                  onClick={() => { setShowCancelModal(false); setCancelTarget(null); }}
                  style={{
                    padding: '12px 20px',
                    background: '#fff', color: '#6b7280',
                    border: '1px solid #e8e4df', borderRadius: 10,
                    fontSize: 14, fontWeight: 500, cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Keep It
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ✅ Overlay removed — notification closes only via X button */}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.9;transform:scale(1.1)} }
        * { box-sizing: border-box; }
        input[type="date"], input[type="time"] { -webkit-appearance: none; }
      `}</style>
    </>
  );
};

export default Worker;
